/**
 * Confidentiality & Intellectual Property — 23 documents.
 *
 * Four different things are being sorted out across this category, and founders routinely
 * conflate them:
 *   confidentiality — they cannot tell anyone
 *   non-use         — they cannot use it themselves
 *   ownership       — it is yours outright, by assignment
 *   licensing       — they own it, and you are permitted to use it
 * Each entry below is written to make the distinction that actually bites for that document.
 */
export const CONFIDENTIALITY_DOCS = [
  {
    id: "mutual-nda",
    name: "Mutual NDA",
    category: "confidentiality",
    subcategory: "Core NDAs",
    aliases: ["Mutual Non-Disclosure Agreement", "Two-Way NDA", "Bilateral NDA", "MNDA"],
    stages: ["idea", "development", "pre-launch", "growth"],
    importance: "essential",
    entityTypes: ["any"],
    parties: "Brand + Counterparty (both disclosing)",
    whenYouNeedIt:
      "Before any conversation where both sides will show each other something they would not post publicly — a co-manufacturer walking you through their line while you walk them through your formula, a potential co-brand partner, an acquirer's early conversation.",
    whatItDoes:
      "Defines what counts as confidential, obliges each side to keep the other's information to itself, limits who inside each company may see it, and sets how long the obligation runs and what happens to the material at the end.",
    whyItMatters:
      "The mutual NDA is the document founders sign most often and read least. Two things decide whether it does anything: whether it stops the other side from using your information as well as repeating it, and how long the obligation actually lasts. A two-year term on a formula conversation means the protection expires around the time your product finally has volume worth copying. Sign enough of these carelessly and you have a written record of everyone you disclosed to, with no one under a meaningful restriction.",
    majorTerms: [
      "Definition of confidential information",
      "Whether oral disclosures must be confirmed in writing",
      "Permitted purpose",
      "Non-use restriction, separate from non-disclosure",
      "Permitted recipients and their obligations",
      "Term of the agreement and survival of the obligation",
      "Return or destruction of materials",
      "Residuals clause, if any",
      "Standard carve-outs (public, already known, independently developed)",
      "Compelled-disclosure procedure",
      "Governing law and forum",
      "Remedies and injunctive relief language",
    ],
    clausesToWatch: [
      {
        clause: "Permitted purpose",
        why: "If the purpose is written broadly — 'evaluating a business relationship' — the other side can argue almost any use of your information falls inside it.",
      },
      {
        clause: "Survival period for confidentiality",
        why: "A flat two- or three-year survival means your formula and cost structure stop being protected on a fixed date regardless of whether they are still secret.",
      },
      {
        clause: "Residuals",
        why: "A residuals clause lets the other side use anything their people remember without notes, which for a formulator or agency is close to permission to reuse the idea.",
      },
      {
        clause: "Requirement that disclosures be marked confidential",
        why: "Most real disclosure in CPG happens on a plant floor or a call, and a marking requirement leaves all of it unprotected.",
      },
      {
        clause: "Return or destruction on request",
        why: "Without it, your spec sheets, COAs and cost models stay in the counterparty's system indefinitely, backed up beyond anyone's ability to delete them.",
      },
      {
        clause: "Carve-out for independently developed information",
        why: "Written loosely, this becomes the argument that they arrived at your formulation approach on their own after seeing yours.",
      },
    ],
    cpgIssues: [
      "Formulas, usage rates and supplier names travel as spreadsheets and spec sheets that get pasted into a counterparty's recipe management or ERP system — say plainly that entering the information into a system is a disclosure and must be covered.",
      "Sending a sample is a disclosure. A finished beverage or bar can be sent to an analytical lab and broken down, so an NDA that covers documents but not physical samples covers the wrong thing.",
      "Cost models, landed cost and co-packer tolling rates are more commercially sensitive than the formula for most early brands, and they are exactly what gets shared in a distributor or retailer conversation.",
      "Trade shows and category reviews create disclosure with dozens of parties in a week; the NDA that governs a category review conversation is often the retailer's form, not yours.",
      "Shelf-life, stability and sensory data are expensive to generate and get shared casually with brokers and buyers who have no obligation to protect them.",
    ],
    founderMistakes: [
      "Using a downloaded mutual NDA with a two-year survival for a formula disclosure, then discovering the protection ran out before the product had national distribution.",
      "Signing the counterparty's form because it was 'just an NDA', and inheriting a residuals clause plus their home-state forum for every future dispute.",
      "Relying on an NDA that prohibits disclosure only, then having no argument when the other side made a similar product themselves without telling anyone.",
      "Never tracking who signed what, so at diligence the buyer's counsel found disclosures to eleven parties and signed agreements with four.",
      "Treating the NDA as the whole protection and handing over the complete formula in the first meeting rather than staging what got shared.",
    ],
    negotiationPoints: [
      "Ask for an explicit non-use covenant tied to a narrowly written permitted purpose, not just a non-disclosure obligation.",
      "Ask for trade secrets and formulas to stay protected for as long as they remain secret, rather than expiring with the general term.",
      "Strike or narrow any residuals clause, particularly with formulators, agencies and labs.",
      "Cover oral and visual disclosures without a written-confirmation requirement, or agree a practical confirmation window.",
      "Add samples, prototypes and physical materials to the definition of confidential information, with a no-analysis and no-reverse-engineering covenant.",
      "Ask for a return-or-destroy obligation with a certification, and accept a narrow archival-backup exception rather than an open-ended one.",
    ],
    redFlags: [
      "A residuals clause combined with a short survival period.",
      "Confidentiality obligations that bind only one side while the agreement is titled 'mutual'.",
      "No non-use restriction anywhere in the document.",
      "A permitted purpose broad enough to include commercialising a similar product.",
      "Assignment of the agreement to any successor without notice, so an acquirer of the counterparty inherits your disclosure.",
      "Forum and governing law in a jurisdiction where enforcement would cost more than the information is worth.",
    ],
    beforeYouSign: [
      "A written list of exactly what you intend to disclose in this conversation, and what you are holding back until later.",
      "A log of every NDA you have signed, with counterparty, date and survival period.",
      "Confirmation that the person signing has authority to bind the counterparty entity, not just a plant or a division.",
    ],
    related: [
      "one-way-nda",
      "non-disclosure-non-use-agreement",
      "manufacturer-nda",
      "trade-secret-protection-agreement",
      "due-diligence-nda",
      "confidentiality-breach-notice",
    ],
    attorneyReview: "depends",
    templateId: "mutual-nda",
    templateVersion: "1.0",
    lastReviewed: "2026-08-25",
  },

  {
    id: "one-way-nda",
    name: "One-Way NDA",
    category: "confidentiality",
    subcategory: "Core NDAs",
    aliases: ["Unilateral NDA", "One-Sided Non-Disclosure Agreement", "Disclosure Agreement"],
    stages: ["idea", "development", "pre-launch", "launch"],
    importance: "essential",
    entityTypes: ["any"],
    parties: "Disclosing Party + Receiving Party",
    whenYouNeedIt:
      "When only you are handing over something worth protecting: showing a formula to a lab, a brand concept to a designer, your cost model to a broker, or your sales data to a prospective hire.",
    whatItDoes:
      "Puts the obligation entirely on the receiving side. You disclose, they hold it, they may only use it for the stated purpose, and they return or destroy it when the conversation ends.",
    whyItMatters:
      "Most early CPG disclosure is one-way, and using a mutual form for it quietly does two harmful things: it gives the other side confidentiality protection they did not need, and it invites them to argue that anything they showed you constrains what you can do next. A designer who becomes a confidential discloser can later claim your packaging direction came from their protected material. Match the paper to the actual flow of information.",
    majorTerms: [
      "Identity of the disclosing party and the protected subject matter",
      "Narrow, written permitted purpose",
      "Non-use as well as non-disclosure",
      "Named permitted recipients inside the receiving company",
      "Term and survival, with longer survival for trade secrets",
      "No-reverse-engineering and no-analysis covenant",
      "Return or destruction with certification",
      "No licence or ownership granted by disclosure",
      "Non-solicitation of your suppliers, if relevant",
      "Injunctive relief acknowledgement",
    ],
    clausesToWatch: [
      {
        clause: "No licence granted",
        why: "Without a clear statement that disclosure grants nothing, a receiving party can argue that being shown a formula came with permission to work on it.",
      },
      {
        clause: "Permitted recipients",
        why: "'Affiliates and advisors' with no obligation to bind them individually means your formula can reach a parent company that also owns a competing brand.",
      },
      {
        clause: "Reverse engineering",
        why: "A receiving party who is sent finished product can deconstruct it unless the agreement says they may not, and that is the realistic route to copying a beverage or a snack.",
      },
      {
        clause: "Term versus survival",
        why: "Confusing the period during which you may disclose with the period the obligation lasts is the single most common drafting error in these forms.",
      },
      {
        clause: "Compelled disclosure",
        why: "Without a notice-and-cooperate procedure, a subpoena or a customer audit becomes an unmonitored release of your specifications.",
      },
    ],
    cpgIssues: [
      "Labs, flavor houses and co-manufacturers all keep formulas in searchable internal systems shared across accounts — the agreement should address access control on their side, not just their good intentions.",
      "A sample sent for a quote is the most under-protected disclosure in CPG; sample handling, retention limits and a no-analysis covenant belong in the document.",
      "Retail buyers and brokers ask for cost, margin and velocity data early, and that data is far more useful to a competitor than the formula is.",
      "Supplier names and usage rates are often the real secret — a competitor who learns which flavor house and which protein supplier you use can approximate the product without ever seeing the formula.",
      "Nutrition panels and ingredient statements published on-pack already reveal part of the formulation, so define what remains confidential rather than claiming everything is.",
    ],
    founderMistakes: [
      "Sending a full formula and a supplier list to three co-manufacturers for quotes with no NDA in place, because the quote request felt like a routine purchasing exercise.",
      "Using a mutual NDA out of habit and handing a designer a confidentiality claim over concepts they later reused for another beverage client.",
      "Setting the agreement to expire after the project rather than having confidentiality survive it, so the obligation ended when the relationship did.",
      "Naming the receiving party as an individual contact rather than their employer, leaving the company that actually holds the file unbound.",
    ],
    negotiationPoints: [
      "Ask for confidentiality on trade secrets to run for as long as the information stays secret, with a shorter fixed period for ordinary business information.",
      "Require that any employee or subcontractor who sees the material is bound by written obligations at least as protective.",
      "Add an express no-analysis, no-deconstruction covenant covering samples and prototypes.",
      "Ask for written certification of destruction, including copies held in email and shared drives.",
      "Where the receiving party works with competitors, ask for a named-account restriction or an internal firewall commitment rather than accepting a general assurance.",
    ],
    redFlags: [
      "The receiving party insisting on the right to work with directly competing brands on the same category with no separation of personnel or files.",
      "A term that ends the confidentiality obligation when the project ends.",
      "Any clause converting disclosure into a licence to use or improve the disclosed material.",
      "Refusal to accept a no-reverse-engineering covenant while asking for physical samples.",
      "Broad permitted recipients including unnamed affiliates.",
    ],
    beforeYouSign: [
      "A staged disclosure plan: what goes out at quote stage, what only goes out after a development agreement is signed.",
      "The correct legal entity name of the receiving party, taken from their invoice or their state registration rather than their website.",
    ],
    related: [
      "mutual-nda",
      "manufacturer-nda",
      "co-packer-nda",
      "recipe-formula-confidentiality-agreement",
      "supplier-nda",
      "trade-secret-protection-agreement",
    ],
    attorneyReview: "depends",
    templateId: "one-way-nda",
    templateVersion: "1.0",
    lastReviewed: "2026-08-25",
  },

  {
    id: "employee-nda",
    name: "Employee NDA",
    category: "confidentiality",
    subcategory: "People and IP",
    aliases: ["Employee Confidentiality Agreement", "Staff NDA", "Employee Non-Disclosure"],
    stages: ["pre-launch", "launch", "growth"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Company + Employee",
    whenYouNeedIt:
      "On or before an employee's first day, and again before giving any employee access to the full formula, the co-packer file, the cost model or the customer list.",
    whatItDoes:
      "Binds the employee to keep company information confidential during and after employment, defines what the company treats as confidential, and sets out what they must return when they leave.",
    whyItMatters:
      "An employee NDA controls what a departing person may repeat — it does not, on its own, decide who owns what they created or stop them from working for a competitor. Founders sign one document and assume all three problems are handled. The operations manager who set up your co-packer relationship leaves with the specifications, the supplier contacts and the tolling rates in their head; the NDA governs the repeating, the invention assignment governs the ownership, and anything about where they can work next is a separate, jurisdiction-sensitive question.",
    majorTerms: [
      "Definition of company confidential information",
      "Duration during and after employment",
      "Return of property and deletion from personal devices",
      "Third-party confidential information the employee must not bring in",
      "Acknowledgement of access levels and need-to-know",
      "Handling of customer, supplier and pricing information",
      "Notification obligation on suspected disclosure",
      "Relationship to the separate invention assignment agreement",
      "Carve-out for lawful communications the employee is entitled to make",
    ],
    clausesToWatch: [
      {
        clause: "Scope of 'confidential information'",
        why: "A definition that sweeps in everything the employee learns is hard to apply in practice and gives no guidance about what actually needs protecting.",
      },
      {
        clause: "Post-employment duration",
        why: "If the obligation ends at a fixed date after departure, your formula and supplier relationships become fair game on that date.",
      },
      {
        clause: "Return and deletion of materials",
        why: "Without a specific obligation covering personal phones, cloud accounts and messaging apps, spec sheets and price lists walk out with everyone.",
      },
      {
        clause: "Interaction with invention assignment",
        why: "If the NDA is silent on ownership, work created by the employee may not sit with the company at all, which shows up during diligence.",
      },
      {
        clause: "Protected-activity carve-out",
        why: "An agreement written to block every kind of communication can create its own problems; this is the clause to have counsel word.",
      },
    ],
    cpgIssues: [
      "The full formula rarely needs to be visible to everyone — production, marketing and sales each need a different slice, and access tiers belong in the onboarding process the NDA references.",
      "Operations staff hold the co-packer's scheduling contacts, tolling rates and yield data, which is the information a competitor would most like to have.",
      "Sales staff hold retailer-specific pricing, deduction history and promotional calendars that are confidential to the retailer as well as to you.",
      "Employees who came from another brand may arrive carrying that brand's specifications; the agreement should expressly bar bringing third-party confidential material in.",
      "Photos and videos taken casually on a plant floor can disclose a co-packer's line configuration, which may breach your own obligations to that co-packer.",
    ],
    founderMistakes: [
      "Papering confidentiality on day one but never doing invention assignment, then finding at diligence that the label artwork and the reformulation work sat with a former employee.",
      "Giving a first ops hire full access to the master formula document because it was easier than maintaining a redacted version.",
      "Letting a departing employee keep a personal phone with the shared drive still synced, and having no written basis to require deletion.",
      "Relying on a handbook acknowledgement instead of a signed agreement, and being unable to show what the employee actually agreed to.",
    ],
    negotiationPoints: [
      "Pair the NDA with a written invention assignment rather than assuming one document covers both.",
      "Ask counsel to size any post-employment restraint to the role and the jurisdiction rather than copying a form.",
      "Set access tiers in writing so the agreement matches what the employee can actually reach.",
      "Include a specific exit checklist obligation: devices, cloud accounts, physical samples and printed specifications.",
    ],
    redFlags: [
      "No signed agreement at all for employees who can see the master formula.",
      "Confidentiality that expires on a fixed date after departure with no trade-secret carve-out.",
      "An agreement drafted for one state used unchanged across a distributed team.",
      "Restrictive covenants copied from another company's template without review.",
    ],
    beforeYouSign: [
      "A written map of who has access to what, so the agreement reflects reality rather than an aspiration.",
      "Counsel's confirmation that any post-employment restriction is appropriate for the employee's location and role.",
    ],
    related: [
      "employee-confidentiality-agreement",
      "employee-ip-assignment-agreement",
      "piia",
      "trade-secret-protection-agreement",
      "contractor-nda",
      "employment-agreement",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "Employment-related restrictions vary sharply by state and country, and a form written for one jurisdiction can create exposure in another.",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "contractor-nda",
    name: "Contractor NDA",
    category: "confidentiality",
    subcategory: "People and IP",
    aliases: ["Freelancer NDA", "Consultant Confidentiality Agreement", "Vendor NDA"],
    stages: ["idea", "development", "pre-launch", "growth"],
    importance: "essential",
    entityTypes: ["any"],
    parties: "Company + Contractor or Freelancer",
    whenYouNeedIt:
      "Before a freelance designer, formulator, photographer, copywriter or fractional operator sees anything about the product — usually before the first working call, not when the invoice arrives.",
    whatItDoes:
      "Obliges an independent contractor to keep your information confidential and to use it only for your project. It is a confidentiality document, not an ownership document.",
    whyItMatters:
      "This is where the confidentiality-versus-ownership confusion costs the most money. A contractor NDA stops the freelance designer from showing your label to anyone else. It does not make the label yours. Absent a written assignment, the person who created the artwork, the photography or the formulation generally holds the rights to it, and paying the invoice does not transfer them. Founders discover this at the worst possible moment — during a trademark filing, a packaging reprint, or an acquirer's IP diligence.",
    majorTerms: [
      "Confidentiality and narrowly written permitted purpose",
      "Non-use of your information on other clients' projects",
      "Obligation to bind their own subcontractors in writing",
      "Return and deletion, including working files",
      "No conflicting engagement in the same product category, if agreed",
      "Term and survival",
      "Express statement that no licence or ownership passes under this document",
      "Cross-reference to the assignment or work-for-hire agreement",
      "Handling of physical samples and prototypes",
    ],
    clausesToWatch: [
      {
        clause: "Statement that ownership is dealt with elsewhere",
        why: "Without it, both sides walk away believing the deliverables question is settled when nothing about ownership has been agreed.",
      },
      {
        clause: "Subcontractor flow-down",
        why: "Freelancers routinely hand work to a retoucher, a 3D renderer or an offshore studio, and none of those people are bound unless the contractor is required to bind them.",
      },
      {
        clause: "Portfolio and self-promotion rights",
        why: "A broad portfolio right lets the contractor publish your unreleased packaging before you launch, or keep showing it after you have moved on.",
      },
      {
        clause: "Non-use across competing clients",
        why: "A formulator or designer with three brands in your category can legitimately reuse an approach unless the agreement restricts use, not just disclosure.",
      },
      {
        clause: "Working files and source materials",
        why: "If only 'confidential information' must be returned, the contractor keeps the layered design files and the bench notes that make the work usable.",
      },
    ],
    cpgIssues: [
      "Freelance formulators frequently work with several brands in one category at once, so a non-use restriction matters more than a non-disclosure restriction.",
      "Designers hold the layered artwork, the dielines and the fonts; a confidentiality agreement alone gets you none of those back.",
      "Photographers shoot product on a set with props and talent, so the confidentiality agreement needs to sit alongside releases before anything can run as an ad.",
      "Contract regulatory and label consultants see your claims substantiation file, which is sensitive to competitors and to retailers alike.",
      "Contractors who visit a co-packer are inside a facility you do not control and may be bound by that facility's own confidentiality obligations at the same time.",
    ],
    founderMistakes: [
      "Getting an NDA signed with a designer, paying for the logo, and later being unable to record a clean chain of title for the trademark because there was no assignment.",
      "Assuming that 'I paid for it' meant the photographer's raw files were company property, then paying again for a licence to run the images as ads.",
      "Letting a freelance formulator keep bench notes and sample retains, which travelled to their next client in the same category.",
      "Signing the contractor's own form, which granted them broad portfolio rights and let unreleased packaging appear publicly before launch.",
    ],
    negotiationPoints: [
      "Pair every contractor NDA with a written assignment or work-for-hire agreement signed at the same time.",
      "Ask for a non-use covenant covering the contractor's other clients in your category for a defined period.",
      "Require delivery of working files and source materials, listed specifically, on completion or termination.",
      "Limit portfolio rights to after the public launch date, and exclude anything showing formulations, costs or supplier names.",
      "Require written flow-down to any subcontractor, with the contractor responsible for their compliance.",
    ],
    redFlags: [
      "A contractor who declines to sign any assignment and offers a licence to their deliverables instead, without saying so plainly.",
      "Portfolio or self-promotion rights that begin immediately and cover unreleased work.",
      "No obligation to return or delete working files.",
      "Confidentiality that binds the individual but not the studio or agency entity that will actually do the work.",
    ],
    beforeYouSign: [
      "The assignment or work-for-hire document, ready to sign in the same package.",
      "A written deliverables list that names the source and working files, not just the final exports.",
      "Confirmation of who else will touch the work and whether they are employees or subcontractors.",
    ],
    related: [
      "work-for-hire-agreement",
      "ip-assignment-agreement",
      "independent-contractor-agreement",
      "design-ownership-agreement",
      "contractor-work-for-hire-agreement",
      "copyright-assignment",
    ],
    attorneyReview: "depends",
    templateId: "contractor-nda",
    templateVersion: "1.0",
    lastReviewed: "2026-08-25",
  },

  {
    id: "manufacturer-nda",
    name: "Manufacturer NDA",
    category: "confidentiality",
    subcategory: "Supply chain confidentiality",
    aliases: ["Manufacturing Confidentiality Agreement", "Factory NDA", "Production NDA"],
    stages: ["development", "pre-launch", "manufacturing"],
    importance: "essential",
    entityTypes: ["any"],
    parties: "Brand + Manufacturer",
    whenYouNeedIt:
      "Before sending a formula, a specification or a sample to any manufacturer — including the ones that will not win the business. Quoting is disclosure.",
    whatItDoes:
      "Restricts the manufacturer from disclosing or using your formula, specifications, process parameters and volumes outside the purpose of quoting and producing for you.",
    whyItMatters:
      "A manufacturer needs your formula in their system to make the product, and once it is there it does not leave. It lives in a recipe management system, on a batch sheet at the line, in a purchasing record showing your ingredient usage rates, and in the memory of a plant manager who runs four other brands in your category. The realistic protection is a non-use covenant, controlled internal access and a defined obligation to purge on exit. Non-disclosure alone protects the least valuable half of the problem.",
    majorTerms: [
      "Non-use restriction covering production for any other party",
      "Coverage of formulas, specifications, process parameters and packaging artwork",
      "Named permitted personnel and internal access controls",
      "Restriction on running your product for a private-label or competing customer",
      "Sample and retain handling",
      "Purge, return and certification on termination",
      "Survival for as long as the information remains secret",
      "Flow-down to their ingredient suppliers and laboratories",
      "No claim to improvements arising from running your product",
    ],
    clausesToWatch: [
      {
        clause: "Ownership of process improvements",
        why: "A manufacturer that claims process refinements developed while running your product can end up owning part of how your product is made.",
      },
      {
        clause: "Non-use versus non-disclosure",
        why: "Without an explicit non-use covenant, nothing stops the plant from producing something extremely similar for another customer.",
      },
      {
        clause: "Internal access control",
        why: "'We keep it confidential' means nothing if the formula is visible to every scheduler and QA technician who also works on a competitor's runs.",
      },
      {
        clause: "Purge obligation on termination",
        why: "If the specification stays in their system after you leave, you have permanently transferred the ability to make your product.",
      },
      {
        clause: "Supplier flow-down",
        why: "Manufacturers pass specifications to ingredient suppliers and testing labs, and those parties are outside your protection unless the flow-down is required.",
      },
    ],
    cpgIssues: [
      "Recipe management and ERP systems retain formulas across customers, so ask specifically how your formula is stored, who can search it and what happens to it if you leave.",
      "Batch sheets on the production floor show the whole formulation to line staff; some manufacturers work from a coded formula for exactly this reason.",
      "Ingredient purchasing records reveal your usage rates and supplier relationships even when the formula itself is coded.",
      "Retained samples and stability retains sit in the plant for the life of the shelf-life study and beyond, and should be covered by name.",
      "Allergen and changeover documentation reveals what else runs on the line, which is confidential to the manufacturer's other customers and can constrain what they can tell you.",
      "Facility audit reports and certification documents you receive are the manufacturer's confidential material — a mutual obligation here is usually fair and worth granting.",
    ],
    founderMistakes: [
      "Sending a complete formula to five plants for quotes on the strength of an emailed assurance, losing control of it before choosing one.",
      "Signing a form that protected disclosure but said nothing about the plant making a similar product for another brand.",
      "Never asking for the formula to be purged after moving production, leaving a live copy at a plant now running a direct competitor.",
      "Accepting a clause giving the manufacturer rights in 'improvements', then finding the process change that fixed a texture problem was not theirs to hand over.",
    ],
    negotiationPoints: [
      "Ask for an explicit non-use covenant that names producing a similar product for a third party as a prohibited use.",
      "Ask for the formula to be held under a code, with the full ingredient breakdown visible only to named individuals.",
      "Require written purge and certification within a defined period after the relationship ends.",
      "Confirm in writing that any process refinement applied to your product does not create rights against you in the formula or the specification.",
      "Require flow-down of equivalent obligations to their suppliers and outside laboratories.",
    ],
    redFlags: [
      "Any claim by the manufacturer to ownership of improvements, modifications or derived formulations.",
      "Refusal to commit to purging your specification after the relationship ends.",
      "No restriction on producing a similar product for another customer.",
      "Confidentiality expiring on a fixed date while your formula is still in their system.",
      "Blanket permission to share your information with unnamed affiliates or sister plants.",
    ],
    beforeYouSign: [
      "A written list of exactly which documents and samples you will be sending, so both sides know what is covered.",
      "The manufacturer's answer, in writing, on where the formula is stored and who can access it.",
      "Confirmation of which other categories the facility runs, to the extent they can tell you.",
    ],
    related: [
      "co-packer-nda",
      "manufacturing-nda",
      "formula-ownership-agreement",
      "co-packer-agreement",
      "trade-secret-protection-agreement",
      "recipe-formula-confidentiality-agreement",
    ],
    attorneyReview: "yes",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "co-packer-nda",
    name: "Co-Packer NDA",
    category: "confidentiality",
    subcategory: "Supply chain confidentiality",
    aliases: ["Copacker NDA", "Contract Packer Confidentiality Agreement", "Tolling NDA"],
    stages: ["development", "pre-launch", "manufacturing"],
    importance: "essential",
    entityTypes: ["any"],
    parties: "Brand + Co-Packer",
    whenYouNeedIt:
      "At the very first co-packer conversation, before the plant tour and before you send a sample or a spec for a tolling quote.",
    whatItDoes:
      "Covers the two-way disclosure that a co-packing relationship actually involves: your formula, specification, volumes and artwork going one way, and their line capability, pricing and customer mix coming back.",
    whyItMatters:
      "Co-packers are the most concentrated confidentiality risk in CPG because they hold everything at once — the formula, the pack format, the run rates, the true cost per case and the launch timing. They also usually run several brands in your category on the same equipment. The commercial consequence of a weak agreement is not just a leaked recipe: a competitor learning your launch date and case volume can time a promotion against you, and a plant that keeps your specification after you leave has quietly kept your product.",
    majorTerms: [
      "Mutual confidentiality covering formula and plant information",
      "Non-use restriction naming production for others",
      "Volumes, forecasts and launch timing treated as confidential",
      "Artwork, dielines and label files covered explicitly",
      "Named-personnel access and firewall commitments",
      "Handling of trial and pilot run material",
      "Purge and certification on termination",
      "Survival for trade secrets beyond the general term",
      "Publicity restriction — no use of your brand as a reference customer without consent",
    ],
    clausesToWatch: [
      {
        clause: "Publicity and reference-customer rights",
        why: "A co-packer listing your brand on their website tells every competitor exactly where your product is made and roughly what it costs to make.",
      },
      {
        clause: "Forecast and volume confidentiality",
        why: "Your forecast is a launch plan; in the hands of a competitor on the same line it is a competitive calendar.",
      },
      {
        clause: "Pilot and trial material",
        why: "Without a clause covering trial runs, samples and off-spec product from a pilot can be retained, tested or, in bad cases, sold.",
      },
      {
        clause: "Firewall between customer teams",
        why: "One scheduler or QA lead serving both you and a competitor is the practical route by which information moves, regardless of what the agreement says.",
      },
      {
        clause: "Purge on exit",
        why: "A co-packer keeping your specification after termination retains the ability to make your product for someone else.",
      },
    ],
    cpgIssues: [
      "Shared lines mean allergen changeover schedules, sanitation windows and run sequencing reveal who else the plant runs and when — that is their customers' confidential information as much as yours.",
      "Tolling rates and yield loss allowances are the numbers a competitor most wants; treat the quote itself as confidential in both directions.",
      "Artwork and dielines sent for print trials sit with the co-packer and often with their label printer, which needs its own flow-down.",
      "Co-packers frequently run private-label programmes for retailers, and a retailer that sees your specification through the plant is a live commercial risk.",
      "Pilot runs generate off-spec product; the agreement should say it is destroyed or returned, not donated, sampled or sold.",
      "Certificates of analysis and lot records created for your product contain the analytical fingerprint of your formulation.",
    ],
    founderMistakes: [
      "Taking a plant tour and describing the full formulation on the floor with no agreement signed, because the meeting was 'just exploratory'.",
      "Allowing the co-packer to name the brand as a reference customer, which put the manufacturing source in front of every competitor in the category.",
      "Never restricting what happened to pilot-run product, and finding trial cases in an unauthorised channel.",
      "Signing a co-packer NDA with a one-year survival, then negotiating the actual supply agreement eighteen months later with the earlier disclosure unprotected.",
    ],
    negotiationPoints: [
      "Ask for a named-personnel list and a commitment that people working on directly competing brands are excluded.",
      "Make forecasts, volumes, pricing and launch dates expressly confidential, not just the formula.",
      "Require destruction of trial and off-spec product with certification.",
      "Prohibit use of your name, logo or product in their marketing without written consent.",
      "Ask for the confidentiality obligation to survive termination for as long as the formula remains secret.",
    ],
    redFlags: [
      "A co-packer unwilling to restrict which of their staff can see the formula.",
      "Blanket permission to disclose to affiliates, sister plants or a parent company.",
      "No restriction on using your brand as a public reference.",
      "Silence on what happens to your specification and artwork after termination.",
      "A confidentiality term shorter than the expected life of the product.",
    ],
    beforeYouSign: [
      "A signed agreement in place before the plant visit, not after it.",
      "Written confirmation of which categories and which competing brands the facility currently runs.",
      "A short list of what you will disclose at quote stage versus after contracting.",
    ],
    related: [
      "manufacturer-nda",
      "co-packer-agreement",
      "manufacturing-nda",
      "formula-ownership-agreement",
      "quality-agreement",
      "product-specification-sheet",
    ],
    attorneyReview: "yes",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "investor-nda",
    name: "Investor NDA",
    category: "confidentiality",
    subcategory: "Core NDAs",
    aliases: ["Investor Confidentiality Agreement", "Fundraising NDA", "Diligence NDA (investor)"],
    stages: ["fundraising", "growth"],
    importance: "situational",
    entityTypes: ["any"],
    parties: "Company + Prospective Investor",
    whenYouNeedIt:
      "Rarely at first contact. It becomes realistic once an investor moves into diligence and asks for the data room: cost build-ups, co-packer terms, customer-level margin and the formula file.",
    whatItDoes:
      "Restricts an investor from disclosing or using the specific commercial and technical material you provide during diligence, usually with carve-outs for their fund's advisors and existing portfolio.",
    whyItMatters:
      "Most professional investors decline to sign an NDA to hear a pitch, and pushing on it early costs you meetings rather than protecting anything. The material that genuinely needs protecting — landed cost, co-packer tolling rates, retailer-specific margin, the formula — is not in the pitch deck and does not need to go out until diligence. Manage the sequence of what you disclose and you solve most of this without a fight; ask a fund to sign a broad NDA before a first call and you mainly signal inexperience.",
    majorTerms: [
      "Scope limited to material actually provided in diligence",
      "Permitted purpose limited to evaluating the investment",
      "Carve-out for the fund's advisors, counsel and accountants under equivalent obligations",
      "Treatment of the fund's existing and future portfolio companies",
      "No standstill or exclusivity implied by the NDA",
      "Term and survival",
      "Return or destruction at the end of diligence",
      "Confidentiality of the fact that discussions are happening",
    ],
    clausesToWatch: [
      {
        clause: "Portfolio carve-out",
        why: "A carve-out permitting the fund to invest in or advise competing companies is normal, but written broadly it can also permit sharing what they learned from you.",
      },
      {
        clause: "Independent development",
        why: "Funds see many companies in a category; a wide independent-development carve-out can swallow the obligation entirely.",
      },
      {
        clause: "Existence of discussions",
        why: "If the fact of the raise is not confidential, word reaching a distributor or a co-packer mid-process can change how they treat you.",
      },
      {
        clause: "Return and destruction",
        why: "Diligence material distributed inside a fund is hard to recall, so the destruction obligation and its archival exception need to be realistic.",
      },
      {
        clause: "Residuals",
        why: "A residuals clause in a fundraising context is close to permission for an associate to carry your commercial model to the next brand they meet.",
      },
    ],
    cpgIssues: [
      "Your data room is a full commercial map: co-packer tolling rates, trade spend by account, deduction history, slotting paid and true landed cost by SKU.",
      "Retailer-specific data is often confidential to the retailer as well, so sharing it may breach a vendor agreement independently of anything you agree with the investor.",
      "Distributor contracts with unfavourable termination terms are exactly what a fund will ask to see, and exactly what a competitor would benefit from knowing.",
      "Formula and specification files rarely need to be in the room at all — a summary with an offer of an on-site review protects the same diligence need.",
      "Velocity and scan data licensed from a syndicated data provider usually carries its own redistribution restrictions.",
    ],
    founderMistakes: [
      "Demanding an NDA before a first meeting and losing the meeting, having disclosed nothing that needed protecting anyway.",
      "Uploading the full formula and the co-packer contract to an open data room at the top of the funnel rather than gating them behind a signed agreement and a serious process.",
      "Sharing retailer-level pricing that the vendor agreement treated as confidential, creating a problem with the account rather than with the investor.",
      "Accepting the fund's form without reading the portfolio carve-out, then finding the same fund backing a directly competing brand months later.",
    ],
    negotiationPoints: [
      "Gate the sensitive material behind a stage rather than a signature: summary first, detail on serious diligence.",
      "Ask for the fact of discussions to be confidential even if the fund will not sign a broad NDA.",
      "Narrow the permitted purpose to evaluating this investment, and exclude use for portfolio company benefit.",
      "Offer an on-site or read-only review of the formula rather than transferring the file.",
    ],
    redFlags: [
      "A residuals clause in any fundraising confidentiality document.",
      "A carve-out permitting disclosure to portfolio companies in your category.",
      "Confidentiality that binds you but not the investor.",
      "Any provision that implies exclusivity, a no-shop or a right of first refusal buried in an NDA.",
    ],
    beforeYouSign: [
      "A tiered data room, with the sensitive tier separated and access logged.",
      "A check of your retailer and distributor agreements for restrictions on sharing their data.",
    ],
    related: [
      "due-diligence-nda",
      "mutual-nda",
      "term-sheet",
      "safe",
      "cap-table-documentation",
      "trade-secret-protection-agreement",
    ],
    attorneyReview: "yes",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "confidentiality-agreement",
    name: "Confidentiality Agreement",
    category: "confidentiality",
    subcategory: "Core NDAs",
    aliases: ["CDA", "Confidential Disclosure Agreement", "Secrecy Agreement"],
    stages: ["idea", "development", "pre-launch", "growth"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Disclosing Party + Receiving Party (or both)",
    whenYouNeedIt:
      "When you need confidentiality terms that will sit inside a larger relationship rather than standing alone — a development agreement, a supply agreement, a joint project — or when a counterparty prefers this label to 'NDA'.",
    whatItDoes:
      "Sets the mechanics of how confidential information is identified, handled, stored and eventually destroyed. In substance it is the same instrument as an NDA; the label carries no separate meaning.",
    whyItMatters:
      "The value is in the mechanics rather than the promise. Whether oral disclosure counts, whether an email attachment needs a legend, who inside the receiving organisation may open the file, how long the duty runs, and what happens on exit — those details decide whether the document does anything. A confidentiality section buried in a supply agreement that ends when the supply agreement ends leaves your formula unprotected the moment the relationship goes wrong, which is exactly when protection matters.",
    majorTerms: [
      "How confidential information is identified and marked",
      "Treatment of oral and visual disclosure",
      "Standard of care required of the receiving party",
      "Permitted internal recipients and need-to-know",
      "Storage, security and subcontractor obligations",
      "Term of disclosure versus survival of the duty",
      "Return, destruction and certification",
      "Compelled disclosure procedure",
      "Remedies, including acknowledgement that damages may be inadequate",
      "Relationship to the surrounding commercial agreement",
    ],
    clausesToWatch: [
      {
        clause: "Survival tied to the main agreement's term",
        why: "Confidentiality that ends when the commercial relationship ends removes protection at the exact moment the counterparty has the least reason to behave well.",
      },
      {
        clause: "Marking and written-confirmation requirements",
        why: "Requiring every disclosure to be labelled means the plant-floor conversation, the sample and the screen-share are all outside the agreement.",
      },
      {
        clause: "Standard of care",
        why: "'The same care they use for their own information' is worth little if the counterparty is careless with their own information.",
      },
      {
        clause: "Definition carve-outs",
        why: "Broad carve-outs for information 'known to the industry' or 'independently developed' can be argued to cover a formulation approach.",
      },
      {
        clause: "Injunctive relief acknowledgement",
        why: "Without it, the practical remedy for a live disclosure is a damages claim after the fact, which does not put the information back.",
      },
    ],
    cpgIssues: [
      "Confidentiality clauses embedded in supply and co-packing agreements typically expire with the agreement — negotiate separate survival for formulas and specifications.",
      "Specification sheets circulate to buyers, brokers, 3PLs and labs, so decide in advance which version of the spec is the shareable one.",
      "Retailers and distributors often impose their own confidentiality terms on your pricing and promotional plans, creating obligations running in both directions.",
      "Sample submissions to certifying bodies, laboratories and competition judges are disclosures that founders rarely paper at all.",
      "Ingredient supplier COAs and technical data sheets are usually the supplier's confidential material and cannot simply be forwarded to a co-packer without permission.",
    ],
    founderMistakes: [
      "Relying on the confidentiality section of a co-packing agreement and never noticing it terminated with the contract.",
      "Marking nothing as confidential despite a marking requirement, leaving the entire disclosure outside the document's protection.",
      "Forwarding a supplier's technical data sheet to a competing supplier for a benchmarking quote, breaching an obligation they did not know they had.",
      "Signing separate, inconsistent confidentiality terms with the same counterparty in three documents, so no one could tell which governed.",
    ],
    negotiationPoints: [
      "Ask for confidentiality to survive the main agreement, with formulas and trade secrets protected for as long as they stay secret.",
      "Replace a strict marking requirement with a practical rule: anything a reasonable person would understand to be confidential.",
      "Set a defined security standard for storage rather than an 'own information' standard.",
      "Add an express acknowledgement that breach may justify injunctive relief.",
    ],
    redFlags: [
      "Survival tied entirely to the term of the commercial agreement.",
      "A definition requiring written designation within a short window after every oral disclosure.",
      "No obligation to return or destroy anything.",
      "Inconsistent confidentiality terms across multiple documents with the same counterparty.",
    ],
    beforeYouSign: [
      "The other agreements with this counterparty, checked for conflicting confidentiality terms.",
      "A decision on which version of your specification is the shareable one.",
    ],
    related: [
      "mutual-nda",
      "non-disclosure-non-use-agreement",
      "one-way-nda",
      "trade-secret-protection-agreement",
      "recipe-formula-confidentiality-agreement",
      "confidentiality-breach-notice",
    ],
    attorneyReview: "depends",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "non-disclosure-non-use-agreement",
    name: "Non-Disclosure and Non-Use Agreement",
    category: "confidentiality",
    subcategory: "Core NDAs",
    aliases: ["NDNU", "Non-Use Agreement", "Non-Disclosure and Non-Circumvention Agreement"],
    stages: ["development", "pre-launch", "manufacturing", "growth"],
    importance: "essential",
    entityTypes: ["any"],
    parties: "Brand + Party receiving formulation or commercial information",
    whenYouNeedIt:
      "Any time the counterparty could realistically make or sell something like your product themselves: a co-manufacturer, a flavor house, a formulator, an ingredient supplier with a private-label arm, a distributor that owns brands.",
    whatItDoes:
      "Adds the covenant most NDAs leave out. Non-disclosure stops them telling anyone. Non-use stops them using it themselves — for their own products, for another customer, or as a starting point for something adjacent.",
    whyItMatters:
      "This is the distinction that costs CPG founders the most and is the least understood. An ordinary NDA is about repeating. It says nothing about the co-manufacturer who never tells a soul and simply starts producing a very similar drink for a customer who walked in after you. Once you are disclosing to parties who are themselves capable of making or selling your product — which is most of the CPG supply chain — non-use is the operative promise and non-disclosure is the secondary one.",
    majorTerms: [
      "Express non-use covenant with a narrow permitted purpose",
      "Definition of a competing or substantially similar product",
      "Non-circumvention covering your suppliers and customers",
      "Duration of the non-use restriction",
      "Named exceptions for the counterparty's genuinely pre-existing products",
      "Evidence obligations for independent development claims",
      "Internal access controls and personnel firewalls",
      "Survival for trade secrets",
      "Remedies and injunctive relief acknowledgement",
    ],
    clausesToWatch: [
      {
        clause: "Independent development exception",
        why: "Without a requirement to show contemporaneous records, the exception becomes an unfalsifiable defence to any non-use claim.",
      },
      {
        clause: "Definition of 'competing product'",
        why: "Defined too narrowly by brand or SKU, it permits an adjacent flavour, format or channel that takes the same shelf space.",
      },
      {
        clause: "Pre-existing product carve-out",
        why: "A carve-out for products the counterparty already sells is fair, but it needs a dated schedule or it will expand later.",
      },
      {
        clause: "Non-circumvention scope",
        why: "If it does not name your suppliers, a broker or consultant can take your ingredient sourcing relationship and rebuild the product elsewhere.",
      },
      {
        clause: "Duration of non-use",
        why: "A short non-use period simply tells the counterparty when they may start, and it is usually shorter than your product's commercial life.",
      },
    ],
    cpgIssues: [
      "Ingredient suppliers and flavor houses often sell to hundreds of brands and can legitimately reuse general know-how; the restriction needs to target your specific formulation and usage rates, not their platform capability.",
      "Co-manufacturers with private-label divisions are simultaneously your supplier and a potential competitor, which is the clearest case for non-use.",
      "Distributors that own or incubate their own brands can see your velocity, pricing and formulation through the normal course of business.",
      "Contract formulators keep bench notes; a non-use covenant should extend to the notes and the approach, not only to the delivered formula.",
      "Retailers running private-label programmes may ask for specification detail during a category review, and that request deserves a non-use commitment before anything moves.",
    ],
    founderMistakes: [
      "Signing a standard NDA with a co-manufacturer that had a private-label division, and having no contractual answer when a similar product appeared under a retailer's own brand.",
      "Accepting a non-use restriction that ran twelve months, which expired before the brand had enough volume to be worth copying.",
      "Letting a broker introduce them to a supplier without a non-circumvention term, then losing the supplier relationship when the broker took it to another client.",
      "Defining the restricted product by exact SKU, so a near-identical variant in a different pack size fell outside the covenant.",
    ],
    negotiationPoints: [
      "Ask for non-use written as its own covenant with its own duration, not folded into the confidentiality clause.",
      "Define the restricted product by category, format and functional positioning rather than by SKU name.",
      "Require contemporaneous written evidence to support any independent-development claim.",
      "Attach a dated schedule of the counterparty's existing products so the pre-existing carve-out cannot grow.",
      "Add non-circumvention naming your co-manufacturer, ingredient suppliers and key accounts.",
    ],
    redFlags: [
      "A counterparty who will accept non-disclosure but refuses any non-use covenant while asking for the full formula.",
      "An independent-development carve-out with no evidentiary requirement.",
      "A pre-existing product carve-out with no schedule and no date.",
      "Non-use expiring well before the product's expected commercial life.",
    ],
    beforeYouSign: [
      "A written description of the product category and adjacent formats you actually need protected.",
      "The counterparty's current product list, dated and attached, if a pre-existing carve-out is being granted.",
      "Confirmation of whether the counterparty has a private-label or own-brand business.",
    ],
    related: [
      "mutual-nda",
      "manufacturer-nda",
      "flavor-house-agreement",
      "formula-ownership-agreement",
      "trade-secret-protection-agreement",
      "ingredient-supplier-development-agreement",
    ],
    attorneyReview: "yes",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "ip-assignment-agreement",
    name: "IP Assignment Agreement",
    category: "confidentiality",
    subcategory: "Ownership and assignment",
    aliases: ["Intellectual Property Assignment", "Assignment of Inventions", "IP Transfer Agreement"],
    stages: ["idea", "development", "pre-launch", "growth", "exit"],
    importance: "essential",
    entityTypes: ["any"],
    parties: "Company + Creator (founder, employee, contractor or agency)",
    whenYouNeedIt:
      "Whenever anything of value was created by a person rather than by the company: the brand name, the logo, the label artwork, the website, the photography, the formulation, the packaging structure.",
    whatItDoes:
      "Transfers ownership of specified intellectual property from the creator to the company outright, with a commitment to sign whatever further paperwork is needed to record the transfer.",
    whyItMatters:
      "Confidentiality controls who may speak. Assignment decides who owns. They are different documents and only one of them survives diligence. The most expensive version of this gap is a brand where the founder personally registered the trademark, a freelance designer holds the artwork, and a formulator holds the recipe — three separate owners of one product, discovered by an acquirer's counsel who then discounts the price or holds back part of it until every chain of title is cleaned up.",
    majorTerms: [
      "Specific description of the assigned intellectual property",
      "Present-tense assignment language, not just a promise to assign later",
      "Consideration supporting the assignment",
      "Further-assurances obligation to sign recordation paperwork",
      "Assignment of the right to sue for past infringement",
      "Waiver of moral rights where applicable and permitted",
      "Delivery of source files, working files and records",
      "Warranty that the creator owns what they are assigning",
      "Carve-out schedule of the creator's pre-existing material",
      "Any licence back to the creator, if agreed",
    ],
    clausesToWatch: [
      {
        clause: "Present assignment versus agreement to assign",
        why: "A promise to assign in the future leaves the company holding a contract right rather than the asset, and it depends on the creator still being cooperative.",
      },
      {
        clause: "Excluded prior inventions schedule",
        why: "A blank or vague schedule invites a later claim that a core element was pre-existing and therefore never transferred.",
      },
      {
        clause: "Further assurances",
        why: "Recording a trademark or copyright assignment often needs extra signatures, and without this obligation you need the creator's goodwill years later.",
      },
      {
        clause: "Consideration",
        why: "An assignment signed long after the work with nothing given in exchange is the version most likely to be challenged.",
      },
      {
        clause: "Delivery of source files",
        why: "Owning the copyright in a label without the layered file and the dieline means paying someone to rebuild an asset you already own.",
      },
      {
        clause: "Licence back to the creator",
        why: "A broad licence back can let a designer or formulator keep using the very thing you paid to own.",
      },
    ],
    cpgIssues: [
      "Packaging is several rights at once — the logo, the illustration, the photography, the copy, the dieline and sometimes the bottle shape — and an assignment naming only 'the design' can miss most of them.",
      "Formulations are typically protected as trade secrets rather than by registration, so the assignment needs to transfer the know-how, the bench records and the specification, not just an abstract right.",
      "Brand names are frequently registered in a founder's personal name at the start; moving the trademark to the company requires its own assignment and a recordation step.",
      "Photography and video are usually licensed rather than assigned by default, so the CPG founder who wants to own the shoot must ask for assignment explicitly.",
      "Product photography that shows an old label becomes commercially unusable after a label revision, whoever owns it — plan asset refresh alongside label changes.",
      "Recipes developed jointly with a co-manufacturer or flavor house may involve inputs the other side genuinely owns; the assignment should be paired with a licence for those elements.",
    ],
    founderMistakes: [
      "Registering the trademark personally at formation and never assigning it to the company, which surfaced during a financing and delayed the close.",
      "Getting an assignment from the freelance designer but not from the retoucher and illustrator they subcontracted to.",
      "Accepting 'all work product belongs to the client' in an email as the assignment, with no schedule, no further-assurances obligation and no source files.",
      "Signing an assignment with a blank prior-inventions schedule, then facing a claim that the core formulation approach was pre-existing.",
      "Never getting the formulation assigned because it was 'just a recipe', and being unable to show ownership of the product's core asset at diligence.",
    ],
    negotiationPoints: [
      "Insist on present-tense assignment language plus a backup obligation to execute further documents.",
      "Require delivery of all source and working files as a condition of final payment.",
      "Complete the prior-inventions schedule specifically, or state that there are none.",
      "Include the right to pursue past infringement, so you can act on copying that began before the assignment.",
      "Where a licence back is requested, limit it to portfolio display after launch and exclude commercial reuse.",
      "Get the assignment signed by the creator's entity and, where a key individual matters, by the individual as well.",
    ],
    redFlags: [
      "Language that only promises a future assignment.",
      "A licence back that permits the creator to reuse the work commercially.",
      "No warranty that the creator owns the material or that it does not incorporate third-party assets.",
      "Stock imagery, fonts or illustrations embedded in deliverables with no licence documentation.",
      "Refusal to hand over layered files, dielines or bench records.",
    ],
    beforeYouSign: [
      "A written inventory of every creative and technical asset and who made it.",
      "The prior-inventions schedule, completed rather than left blank.",
      "Licence documentation for any third-party fonts, stock images or illustrations used in the work.",
      "Confirmation of who inside the creator's business actually touched the work.",
    ],
    related: [
      "work-for-hire-agreement",
      "copyright-assignment",
      "trademark-assignment",
      "founder-ip-assignment",
      "design-ownership-agreement",
      "formula-assignment-agreement",
    ],
    attorneyReview: "yes",
    templateId: "ip-assignment-agreement",
    templateVersion: "1.0",
    lastReviewed: "2026-08-25",
  },

  {
    id: "work-for-hire-agreement",
    name: "Work-for-Hire Agreement",
    category: "confidentiality",
    subcategory: "Ownership and assignment",
    aliases: ["Work Made for Hire Agreement", "Commissioned Work Agreement", "WFH Agreement"],
    stages: ["idea", "development", "pre-launch", "growth"],
    importance: "essential",
    entityTypes: ["any"],
    parties: "Company + Creator or Studio",
    whenYouNeedIt:
      "When commissioning creative work you intend to own from the moment it exists: label artwork, brand identity, product photography, video, website design, illustration and copy.",
    whatItDoes:
      "Characterises commissioned work as made for hire so that the commissioning company is treated as the owner from creation, and — in any well-drafted version — adds a present assignment as a backstop for anything the work-for-hire characterisation does not reach.",
    whyItMatters:
      "Founders treat 'work for hire' as a magic phrase that makes everything theirs. It is a defined concept in copyright with limits on which kinds of commissioned work it covers, which is exactly why professionally drafted agreements always pair it with an outright assignment. A packaging project that produces artwork, photography, a dieline, copy and a font pairing does not sit neatly inside one characterisation. Rely on the label alone and the parts it does not reach stay with the creator.",
    majorTerms: [
      "Work-for-hire characterisation of the deliverables",
      "Backup present assignment of anything not covered by it",
      "Definition of deliverables including source and working files",
      "Payment schedule tied to delivery of files",
      "Warranty of originality and non-infringement",
      "Third-party asset licences: fonts, stock, illustrations, music",
      "Waiver of moral rights where applicable and permitted",
      "Portfolio and credit rights",
      "Confidentiality and pre-launch embargo",
      "Subcontractor flow-down obligations",
    ],
    clausesToWatch: [
      {
        clause: "Absence of a backup assignment",
        why: "If the work-for-hire characterisation does not reach a particular deliverable, an agreement without an assignment leaves that deliverable with the creator.",
      },
      {
        clause: "Third-party assets inside the deliverable",
        why: "A label built on a licensed font or a stock illustration is only as transferable as those underlying licences allow, and desktop licences rarely cover packaging or mass reproduction.",
      },
      {
        clause: "Definition of deliverables",
        why: "'Final files' commonly means flattened exports; without naming layered files, dielines and project files you own an image you cannot edit.",
      },
      {
        clause: "Payment-linked ownership",
        why: "A clause making ownership conditional on final payment means a billing dispute becomes an ownership dispute at the printer's deadline.",
      },
      {
        clause: "Portfolio and credit rights",
        why: "Unrestricted portfolio rights can put your unreleased packaging on a public site before launch.",
      },
      {
        clause: "Subcontractor flow-down",
        why: "The illustrator or retoucher the studio hired is the actual creator, and their rights only reach you if the studio was required to secure them.",
      },
    ],
    cpgIssues: [
      "Packaging artwork must ultimately be handed to a printer as production-ready files with a dieline that matches the converter's specification — owning a flattened PDF is not enough.",
      "Font licensing is the most common hidden defect in packaging: a licence covering desktop use may not cover mass-reproduced packaging or a logo mark.",
      "Product photography shot for launch is usually reused across Amazon, retailer portals and paid ads, and the agreement should not restrict channels the way a licence would.",
      "Retailers require specific artwork formats and dielines for their own packaging portals, so you need editable files, not just the printer's proof.",
      "Illustration commissioned for a limited edition often reappears on the standard pack later; scope the ownership to cover the reuse you can foresee.",
      "Nutrition panel and ingredient statement layout is regularly rebuilt at every label revision, which needs the working file and a live relationship or clean ownership.",
    ],
    founderMistakes: [
      "Signing a one-page work-for-hire form with no backup assignment and no source-file delivery, then paying a second studio to rebuild the label from scratch for a reprint.",
      "Discovering at print stage that the logo used a font whose licence did not cover packaging reproduction, forcing a redraw days before a production window.",
      "Accepting a clause tying ownership to final payment, then losing access to the artwork during a fee dispute with a printer deadline looming.",
      "Letting a studio post unreleased packaging to their portfolio, which reached a competitor before the product shipped.",
      "Never obtaining rights from the subcontracted illustrator, so the character on the pack was owned by someone with no contract with the company.",
    ],
    negotiationPoints: [
      "Require both work-for-hire characterisation and a present assignment as a backstop in the same document.",
      "List deliverables explicitly, including layered files, dielines, fonts used and any 3D or render files.",
      "Ask for a warranty of originality plus disclosure of every third-party asset with its licence terms.",
      "Move ownership to delivery rather than to final payment, and hold a retention instead.",
      "Restrict portfolio use until after public launch and exclude anything showing formulation or cost.",
      "Require the studio to obtain equivalent rights from every subcontractor and to evidence it on request.",
    ],
    redFlags: [
      "Work-for-hire language with no assignment fallback.",
      "Ownership conditional on payment in full.",
      "No disclosure of fonts, stock or third-party illustration used in the deliverables.",
      "Deliverables defined as final exports only.",
      "A creator who reserves the right to reuse elements of the design for other clients.",
    ],
    beforeYouSign: [
      "A deliverables schedule naming file types, not just outcomes.",
      "The list of third-party assets and their licences, with confirmation the licences cover packaging and paid media.",
      "Names of every subcontractor who will touch the work.",
    ],
    related: [
      "ip-assignment-agreement",
      "copyright-assignment",
      "design-ownership-agreement",
      "packaging-design-agreement",
      "contractor-work-for-hire-agreement",
      "packaging-artwork-ownership",
    ],
    attorneyReview: "yes",
    templateId: "work-for-hire-agreement",
    templateVersion: "1.0",
    lastReviewed: "2026-08-25",
  },

  {
    id: "trademark-assignment",
    name: "Trademark Assignment",
    category: "confidentiality",
    subcategory: "Ownership and assignment",
    aliases: ["Assignment of Trademark", "Brand Name Assignment", "Mark Transfer Agreement"],
    stages: ["idea", "pre-launch", "growth", "exit"],
    importance: "essential",
    entityTypes: ["any"],
    parties: "Owner of the mark + Company (or Buyer)",
    whenYouNeedIt:
      "When the brand name, logo or a filed application sits with anyone other than the operating company: a founder personally, a prior partnership, a naming agency, a departed co-founder, or a seller in an acquisition.",
    whatItDoes:
      "Transfers ownership of a trademark or an application, together with the goodwill associated with it, and provides the paperwork needed to record the change with the relevant trademark office.",
    whyItMatters:
      "The brand name is usually the single most valuable thing a CPG company owns, and it is the asset most often sitting in the wrong name. Founders file personally because the entity did not exist yet, then never move it. A mismatch between the registered owner and the company that actually sells the product is a diligence finding in every financing and every sale, and it is the kind of finding that holds money back at closing rather than merely generating a comment.",
    majorTerms: [
      "Identification of each mark, application or registration number",
      "Transfer of the mark together with its associated goodwill",
      "Effective date and consideration",
      "Further-assurances obligation for recordation",
      "Assignment of the right to pursue past infringement",
      "Warranty of ownership and of no prior encumbrance",
      "Treatment of any pending applications and deadlines",
      "Domain names, social handles and related assets included or excluded",
      "Any licence back to the assignor",
    ],
    clausesToWatch: [
      {
        clause: "Goodwill language",
        why: "Trademark assignments are conventionally made together with the goodwill of the business; an assignment drafted without it invites a challenge to the transfer.",
      },
      {
        clause: "Schedule of marks",
        why: "A schedule that lists the word mark but not the logo, the stylised version or foreign filings leaves parts of the brand behind.",
      },
      {
        clause: "Further assurances and recordation",
        why: "Without a continuing obligation to sign, a later filing requirement depends on the goodwill of someone who may have left.",
      },
      {
        clause: "Domains and social handles",
        why: "These are not trademarks and do not transfer automatically; if they are not listed, they stay with the assignor.",
      },
      {
        clause: "Past infringement",
        why: "Copying that started before the assignment may be outside your reach unless the right to act on it is transferred with the mark.",
      },
    ],
    cpgIssues: [
      "The mark must be assigned for the classes that actually cover your goods; a mark filed for beverages may not extend to a supplement or apparel line extension.",
      "Co-branding and collaboration packs create combined marks whose ownership needs settling before the pack prints.",
      "A distributor or importer who registered your mark abroad to 'help' becomes an obstacle to entering that market later.",
      "Retailer listing systems and Amazon brand registration key off the registered owner, so a mismatch can block enrolment and brand protection tools.",
      "Contract manufacturers occasionally register a mark used on a private-label version of your product, which needs untangling before any brand sale.",
    ],
    founderMistakes: [
      "Filing the application in a personal name before incorporating and never assigning it, then finding the financing could not close until it was fixed.",
      "Assigning the word mark but forgetting the stylised logo filing, which stayed in a former partner's name.",
      "Letting an international distributor register the mark in their country, which turned into a payment demand when the relationship ended.",
      "Transferring the mark without the domain and the social handles, which remained with a departed co-founder.",
    ],
    negotiationPoints: [
      "List every mark, application, class and jurisdiction on a schedule rather than describing the brand generally.",
      "Include domains, social handles and any brand-registry enrolments in the same transfer.",
      "Ask for the right to pursue infringement that began before the assignment date.",
      "Include a broad further-assurances covenant, since recordation steps can arise years later.",
      "Where the assignor is an individual founder, sign the assignment at formation rather than at the first financing.",
    ],
    redFlags: [
      "An assignment that transfers the mark without reference to goodwill.",
      "A general description of the brand instead of a schedule of filings.",
      "No warranty that the mark is unencumbered by a prior licence or security interest.",
      "A licence back permitting the assignor to keep using the mark in any category.",
      "Silence on foreign filings when the brand already exports.",
    ],
    beforeYouSign: [
      "A current list of every application and registration, with numbers, classes and jurisdictions.",
      "Confirmation of who is recorded as owner today, taken from the register rather than from memory.",
      "A list of domains, handles and brand-registry accounts to move at the same time.",
    ],
    related: [
      "ip-assignment-agreement",
      "copyright-assignment",
      "trademark-license-agreement",
      "ip-purchase-agreement",
      "brand-acquisition-agreement",
      "ip-cease-and-desist",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "Trademark ownership, goodwill and recordation formalities are technical and jurisdiction-specific, and a defective assignment is usually discovered when it is most expensive to fix.",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "copyright-assignment",
    name: "Copyright Assignment",
    category: "confidentiality",
    subcategory: "Ownership and assignment",
    aliases: ["Assignment of Copyright", "Creative Works Assignment", "Content Ownership Transfer"],
    stages: ["pre-launch", "launch", "growth", "exit"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Author or Studio + Company",
    whenYouNeedIt:
      "When creative material you rely on commercially was made by someone else and you need to own it rather than licence it: label illustrations, photography, video, website content, packaging copy, brand book material.",
    whatItDoes:
      "Transfers ownership of copyright in identified works from the author to the company, ideally with delivery of the underlying files and a warranty that the work is original.",
    whyItMatters:
      "This is the ownership-versus-licensing line drawn precisely. A licence lets you use the photograph on the terms someone else sets, for a period they choose, in the channels they name. An assignment makes it yours to use anywhere, forever, including on packaging and in an eventual sale of the brand. Founders often pay assignment prices for licence rights because nobody named which one they were buying, and the gap only appears when a retailer asks for artwork rights or a buyer's counsel asks who owns the imagery on the pack.",
    majorTerms: [
      "Schedule identifying the works being assigned",
      "Present assignment of all rights, worldwide, for the full term",
      "Delivery of source, raw and working files",
      "Warranty of originality and of clearance for third-party elements",
      "Waiver of moral rights where applicable and permitted",
      "Right to modify, crop, recolour and create derivative works",
      "Assignment of the right to pursue past infringement",
      "Credit and portfolio arrangements, if any",
      "Consideration and further assurances",
    ],
    clausesToWatch: [
      {
        clause: "Right to modify and create derivatives",
        why: "Without it, an assignment can still leave an argument about recolouring, cropping for a retailer template, or adapting the artwork to a new pack size.",
      },
      {
        clause: "Third-party elements",
        why: "A photograph containing a recognisable person, a licensed prop or another brand's product carries obligations the author cannot assign away.",
      },
      {
        clause: "Delivery of raw files",
        why: "Assigning the copyright in an image while keeping the raw capture files means you own something you cannot re-edit or re-master.",
      },
      {
        clause: "Credit obligations",
        why: "A mandatory credit line on every use is impractical on packaging and in paid social, and becomes a breach you commit constantly.",
      },
      {
        clause: "Scope limited to identified deliverables",
        why: "Outtakes, alternate cuts and unused concepts often become the most useful assets later, and a narrow schedule leaves them behind.",
      },
    ],
    cpgIssues: [
      "Product photography is reused across DTC, Amazon, retailer portals, trade sell sheets and paid ads — a channel-limited licence breaks at the first retailer listing.",
      "Label artwork must be adaptable to multiple pack formats and to regulatory label revisions, so the right to modify matters as much as the right to use.",
      "Images showing an outdated ingredient statement, claim or pack become unusable after a reformulation regardless of who owns them.",
      "Recipe and serving-suggestion content produced for a brand often features other companies' products in frame, which carries its own clearance issues.",
      "User-generated content reposted from customers is not covered by any of this, and needs its own permission before it appears in paid media.",
      "Video shot at a co-packer's facility may show that facility's equipment and layout, which the co-packer may treat as confidential.",
    ],
    founderMistakes: [
      "Paying for a photo shoot on a standard licence, then having to renegotiate for every new channel including Amazon and paid social.",
      "Owning the final images but not the raw files, so re-editing for a new pack shape meant a reshoot.",
      "Assigning only the delivered selects and losing access to the outtakes that would have covered a later campaign.",
      "Using a photograph containing a recognisable bystander with no release, and pulling the campaign after a complaint.",
    ],
    negotiationPoints: [
      "Buy the assignment for material that will appear on packaging or run as paid media; licence the rest if budget requires.",
      "Include outtakes, alternates and raw capture files in the schedule.",
      "Secure an express right to modify, crop and create derivative works.",
      "Require disclosure and clearance evidence for every third-party element appearing in the work.",
      "Replace mandatory credit obligations with a discretionary credit where the author needs recognition.",
    ],
    redFlags: [
      "An assignment limited to specified channels or a specified period — that is a licence wearing an assignment's title.",
      "No warranty of originality or of clearance for people, places and props shown.",
      "Retention of raw files by the author.",
      "A credit requirement that applies to packaging and advertising.",
    ],
    beforeYouSign: [
      "A schedule listing the works, including outtakes and raw material.",
      "Signed model and property releases for everything appearing in the images.",
      "Confirmation that the author, not a subcontracted shooter or retoucher, holds the rights being assigned.",
    ],
    related: [
      "ip-assignment-agreement",
      "work-for-hire-agreement",
      "content-release",
      "photography-agreement",
      "model-release",
      "design-ownership-agreement",
    ],
    attorneyReview: "yes",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "product-development-ip-agreement",
    name: "Product Development IP Agreement",
    category: "confidentiality",
    subcategory: "Formula and product IP",
    aliases: ["Development IP Ownership Agreement", "Joint Development IP Terms", "Foreground IP Agreement"],
    stages: ["development", "pre-launch", "manufacturing"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Brand + Development Partner (lab, formulator, supplier or manufacturer)",
    whenYouNeedIt:
      "Before development work starts with any partner who brings their own technology, platform or know-how to the table — which is nearly all of them.",
    whatItDoes:
      "Separates what each side already owned (background IP) from what the project creates (foreground IP), assigns the foreground, and licenses whatever background is needed so the foreground is actually usable.",
    whyItMatters:
      "This is the entry where ownership and licensing have to work together rather than compete. A lab that has spent years developing an emulsion platform will not assign that platform to you, and should not have to. What you need is ownership of the formula built on it plus a licence broad enough that the formula can be made, moved and eventually sold with the business. Get only the assignment and you own a formula you cannot legally produce; get only a licence and you own nothing at all.",
    majorTerms: [
      "Definition and dated schedule of each party's background IP",
      "Definition of foreground IP arising from the project",
      "Assignment of foreground IP to the brand",
      "Licence to background IP embedded in the deliverable",
      "Scope of that licence: perpetual, worldwide, sublicensable to manufacturers",
      "Transferability of the licence on a sale of the business",
      "Ownership of improvements and modifications",
      "Rights to use the deliverable with alternative manufacturers",
      "Publication and reference-customer restrictions",
      "Escrow or deposit of specifications, if agreed",
    ],
    clausesToWatch: [
      {
        clause: "Background IP schedule",
        why: "An undated or open-ended schedule lets a partner reclassify project output as pre-existing platform technology after the fact.",
      },
      {
        clause: "Sublicence rights to your manufacturers",
        why: "A licence you cannot pass to a co-packer means you own a formula that no third party is permitted to produce.",
      },
      {
        clause: "Transferability on a change of control",
        why: "A licence that terminates or requires consent on a sale of the business becomes a live issue in every acquisition.",
      },
      {
        clause: "Ownership of improvements",
        why: "A partner who owns improvements to your own formula can end up controlling the version you actually sell.",
      },
      {
        clause: "Field-of-use limits",
        why: "A licence limited to one format or channel blocks line extensions into powder, ready-to-drink or foodservice later.",
      },
    ],
    cpgIssues: [
      "Development partners commonly hold real platform IP — encapsulation, emulsion systems, masking technology, shelf-stable protein systems — and a blanket demand for ownership will end the conversation rather than win the point.",
      "The deliverable must be producible at a third-party plant, so the licence needs to reach co-manufacturers, not just your own facility.",
      "Line extensions and format changes are the normal path in CPG, so field-of-use limits by format tend to bite within a year.",
      "Analytical methods and testing protocols developed for the product are part of the foreground and are needed by whichever lab tests future lots.",
      "Regulatory dossiers, claims substantiation and stability data created during development should follow the formula, not stay with the partner.",
    ],
    founderMistakes: [
      "Signing a development agreement that assigned the formula but gave no right to sublicense the partner's process to a co-packer, stalling the first commercial run.",
      "Accepting a background IP schedule attached 'to be completed later', which was completed after the project succeeded and covered more than expected.",
      "Failing to secure transferability, so an acquirer required a consent the partner priced at closing.",
      "Leaving improvements with the partner, then finding the reformulated version that fixed a stability problem was not theirs to take elsewhere.",
    ],
    negotiationPoints: [
      "Attach the background IP schedule, dated and signed, before work starts.",
      "Ask for a perpetual, worldwide, transferable licence to any background IP embedded in the deliverable, sublicensable to your manufacturers.",
      "Ask for improvements to your formula to be assigned to you, and for improvements to their platform to stay with them but remain licensed to you.",
      "Remove field-of-use limits tied to format, or price a defined expansion right now.",
      "Where the partner will not assign a critical element, ask for specification escrow and second-source rights instead.",
    ],
    redFlags: [
      "No background IP schedule at all.",
      "A licence that ends when the commercial relationship ends.",
      "Consent required for transfer on a sale of the company.",
      "Partner ownership of improvements to your formula.",
      "A right for the partner to publish or present the development work.",
    ],
    beforeYouSign: [
      "The dated background IP schedule from both sides.",
      "A written answer to one question: with this signed, can a different co-packer legally make this product tomorrow?",
      "Confirmation of who will hold the analytical methods and stability data.",
    ],
    related: [
      "formula-ownership-agreement",
      "product-development-agreement",
      "rd-agreement",
      "flavor-house-agreement",
      "formula-assignment-agreement",
      "product-development-nda",
    ],
    attorneyReview: "yes",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "formula-ownership-agreement",
    name: "Formula Ownership Agreement",
    category: "confidentiality",
    subcategory: "Formula and product IP",
    aliases: ["Recipe Ownership Agreement", "Formulation Ownership Agreement", "Formula Rights Agreement"],
    stages: ["development", "pre-launch", "manufacturing", "exit"],
    importance: "essential",
    entityTypes: ["any"],
    parties: "Brand + Formulator, Lab or Manufacturer",
    whenYouNeedIt:
      "Before development starts, and again before a co-manufacturer makes any change to a formula that is already yours. It is also the cleanup document when a product was developed with no paper at all.",
    whatItDoes:
      "States who owns the formula, the specification, the process parameters and any modification to them, and gives the brand the right to take that package to a different manufacturer.",
    whyItMatters:
      "Ownership of the formula is what makes you a brand rather than a customer. The trap is not usually an outright ownership grab — it is the improvement clause. A co-manufacturer adjusts a stabiliser to fix separation, or a lab reworks the acid balance to hold flavour through the shelf life, and a quiet clause gives them ownership of that change. You still own the original. What you sell is now the improved version, and it belongs to someone else. Every plant you approach afterwards is quoting on a formula you cannot fully hand them.",
    majorTerms: [
      "Ownership of the formula, specification and process parameters",
      "Ownership of modifications, improvements and derivatives",
      "Right to transfer production to another manufacturer",
      "Delivery of the complete formula, including supplier names and usage rates",
      "Treatment of compounded ingredients supplied under a code",
      "Confidentiality and non-use obligations",
      "Access to analytical methods and testing protocols",
      "Escrow or deposit of the specification",
      "Assignment of the formula on a sale of the business",
      "Survival beyond termination of any supply relationship",
    ],
    clausesToWatch: [
      {
        clause: "Ownership of improvements",
        why: "This is the clause that separates owning the formula on paper from owning the formula you actually sell.",
      },
      {
        clause: "Definition of 'the formula'",
        why: "If the definition covers a bill of materials but not process parameters, times, temperatures and order of addition, a new plant cannot reproduce the product.",
      },
      {
        clause: "Coded or compounded ingredients",
        why: "A formula listing 'Flavor 4471' is not a formula you can move; you own a document that points at someone else's proprietary ingredient.",
      },
      {
        clause: "Right to transfer production",
        why: "Ownership without an express right to give the formula to another manufacturer invites an argument at the exact moment you want to leave.",
      },
      {
        clause: "Survival on termination",
        why: "Ownership terms that sit inside a supply agreement and expire with it hand the question back to the counterparty on exit.",
      },
      {
        clause: "Assignment on a change of control",
        why: "A formula that cannot travel with the business in a sale reduces what the business is worth.",
      },
    ],
    cpgIssues: [
      "There is no registration that makes a recipe yours. What protects a formula in practice is that very few people know it, which means the protection lives in contracts and access control and weakens every time the circle widens.",
      "Most beverage and snack formulas contain at least one compounded input — a flavor system, a colour blend, a protein premix — that the supplier owns and sells as an ingredient, so full ownership of the whole build is often not available.",
      "Process parameters are as important as the ingredient list: pasteurisation profile, homogenisation pressure, fill temperature, headspace and carbonation volumes all change the product.",
      "Scale-up changes at a new plant are effectively reformulation, so decide in advance who owns the adjusted version.",
      "Stability and shelf-life data are tied to a specific formula and process, and moving plants without that data means repeating the study before a retailer will accept the product.",
      "Allergen and claims status can change with a supplier substitution, so a modification clause interacts with your label as well as your ownership.",
    ],
    founderMistakes: [
      "Assuming that paying for development meant owning the result, and finding no assignment anywhere in the file when they tried to move plants.",
      "Signing a co-packing agreement whose improvement clause gave the plant ownership of the very changes that made the product commercially viable.",
      "Accepting a formula document that named a flavor code rather than the flavor supplier and specification, which made the formula untransferable.",
      "Letting the co-packer hold the only complete version of the specification, including process parameters that were never written down anywhere else.",
      "Never getting the formula assigned from the individual formulator to the company, leaving an individual in the chain of title at diligence.",
    ],
    negotiationPoints: [
      "Ask for ownership of the formula and of every modification, improvement and derivative, however they arise.",
      "Define the formula to include process parameters, equipment assumptions, analytical methods and supplier identities.",
      "Where a compounded ingredient cannot be assigned, ask for a transferable supply commitment and specification escrow instead.",
      "Secure an express right to disclose the full formula to any alternative manufacturer under confidentiality.",
      "Put the ownership terms in a standalone document that survives termination of the supply relationship.",
      "Require delivery of a complete, current specification package annually, not just at the start.",
    ],
    redFlags: [
      "Any clause granting the other side ownership of, or a licence to, improvements.",
      "A formula document that identifies key inputs only by code.",
      "Ownership terms that terminate with the supply agreement.",
      "Refusal to permit disclosure of the formula to an alternative manufacturer.",
      "A requirement that any reformulation be performed exclusively by the counterparty.",
      "Consent required to assign the formula in a sale of the business.",
    ],
    beforeYouSign: [
      "The complete current formula and specification package in your own possession, not just on the counterparty's system.",
      "A list of every compounded or coded ingredient and who owns each one.",
      "The written answer to whether a second manufacturer could produce this product from the documents you hold today.",
      "Confirmation that the individual formulator has assigned their rights to whichever entity is signing.",
    ],
    related: [
      "formula-assignment-agreement",
      "flavor-house-agreement",
      "recipe-formula-confidentiality-agreement",
      "co-packer-agreement",
      "product-development-ip-agreement",
      "trade-secret-protection-agreement",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "Formula ownership determines whether the business can change manufacturers or be sold, and the improvement and modification language is where that outcome is actually decided.",
    templateId: "formula-ownership-agreement",
    templateVersion: "1.0",
    lastReviewed: "2026-08-25",
  },

  {
    id: "recipe-formula-confidentiality-agreement",
    name: "Recipe / Formula Confidentiality Agreement",
    category: "confidentiality",
    subcategory: "Formula and product IP",
    aliases: ["Recipe NDA", "Formula Secrecy Agreement", "Proprietary Recipe Agreement"],
    stages: ["idea", "development", "pre-launch", "manufacturing"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Brand + Anyone who will see the formula",
    whenYouNeedIt:
      "Before the formula itself — not a description of the product, the actual ingredient list with usage rates — goes to a lab, a plant, a chef, a consultant or an employee.",
    whatItDoes:
      "Applies confidentiality and non-use obligations specifically to the recipe or formula, with handling rules that match how a formula actually moves: files, batch sheets, samples and conversations.",
    whyItMatters:
      "A recipe is generally protected by keeping it secret rather than by any registration, so the protection is only as good as the discipline around it. That makes this a document about handling, not just promises. Who gets the full version and who gets a coded version, whether it may be printed on the plant floor, whether samples may be analysed, how it is stored, and what has to happen when someone leaves — those rules are the protection. Once a formula has circulated widely, no agreement puts it back.",
    majorTerms: [
      "Precise definition of the protected formula and specification",
      "Non-use as well as non-disclosure",
      "Named individuals permitted to see the full version",
      "Coded versus full disclosure protocol",
      "Restrictions on printing, copying and storage location",
      "No-analysis and no-reverse-engineering covenant on samples",
      "Sample and retain handling and destruction",
      "Survival for as long as the formula remains secret",
      "Purge and certification on termination",
    ],
    clausesToWatch: [
      {
        clause: "Named-individual access",
        why: "A promise from a company means little without a list of the specific people who may open the file and a duty to update it.",
      },
      {
        clause: "No-analysis covenant",
        why: "Without it, sending finished product for a quote is an invitation to deconstruct the formulation lawfully.",
      },
      {
        clause: "Survival period",
        why: "A formula does not stop being secret on a calendar date, so a fixed expiry sets the day your protection ends for no commercial reason.",
      },
      {
        clause: "Copying and storage restrictions",
        why: "A formula emailed to a personal account or printed for a line supervisor has left the controlled circle regardless of what the agreement promises.",
      },
      {
        clause: "Purge on exit",
        why: "The end of the relationship is when the formula is most likely to be used, and the least likely to be deleted.",
      },
    ],
    cpgIssues: [
      "Common practice is to disclose in layers: a coded formula for quoting, the full build only after a development or supply agreement is signed.",
      "Batch sheets on the plant floor expose the whole formulation to line staff, which is why some manufacturers run coded pre-blends for sensitive brands.",
      "Purchasing records reveal usage rates even when the formula is coded, so ingredient volumes deserve their own confidentiality treatment.",
      "Nutrition panels, allergen statements and ingredient declarations published on-pack already disclose part of the build — define what is still secret rather than claiming everything is.",
      "Certificates of analysis and third-party lab reports contain analytical values that narrow the possible formulations considerably.",
      "Chefs and culinary consultants routinely publish and teach, so their agreements need explicit restrictions on demonstrating or writing about your recipe.",
    ],
    founderMistakes: [
      "Emailing the full formula to a plant contact's personal address for convenience, permanently losing control of where it sits.",
      "Sending finished product for benchmarking with no no-analysis covenant, and seeing a very similar product from the same recipient months later.",
      "Setting a three-year confidentiality period on the formula because that was what the template said.",
      "Giving every new operations hire access to the complete formula file rather than a role-appropriate version.",
    ],
    negotiationPoints: [
      "Ask for confidentiality on the formula to last as long as it stays secret, separate from any general term in the agreement.",
      "Require a named-individual access list, updated in writing when personnel change.",
      "Add an express no-analysis and no-deconstruction covenant covering all samples and finished product.",
      "Agree a coded-disclosure protocol for quoting, with the full build released only after contracting.",
      "Require certified purge of the formula from all systems within a defined period after termination.",
    ],
    redFlags: [
      "A fixed expiry on formula confidentiality.",
      "No non-use covenant.",
      "Refusal to accept a no-analysis restriction on samples.",
      "Unrestricted internal distribution within the counterparty's organisation.",
      "No obligation to delete the formula from systems on exit.",
    ],
    beforeYouSign: [
      "Your own decision about which version of the formula this counterparty actually needs.",
      "A record of who currently holds a full copy, inside and outside the company.",
    ],
    related: [
      "formula-ownership-agreement",
      "trade-secret-protection-agreement",
      "manufacturer-nda",
      "non-disclosure-non-use-agreement",
      "flavor-house-agreement",
      "food-scientist-agreement",
    ],
    attorneyReview: "yes",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "trade-secret-protection-agreement",
    name: "Trade Secret Protection Agreement",
    category: "confidentiality",
    subcategory: "Formula and product IP",
    aliases: ["Trade Secret Policy", "Confidential Information Protection Program", "Secrecy Protocol"],
    stages: ["development", "manufacturing", "growth", "exit"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Company + Employees, Contractors and Supply Chain Partners",
    whenYouNeedIt:
      "Once more than a handful of people can reach the formula, the cost model or the supplier list — usually around the first operations hire, the second co-manufacturer, or the first time an acquirer asks how the formula is protected.",
    whatItDoes:
      "Sets out the company's own programme for protecting information it treats as secret: what is designated, who may access it, how it is stored and marked, and what employees and partners must do on exit.",
    whyItMatters:
      "Everything else in this category is a promise from someone else. This is the part you control. Protection for a formula depends on it actually being kept secret, which is a question of behaviour rather than paperwork: whether the master file is shared by link, whether the plant prints batch sheets, whether departing employees are debriefed. In an acquisition, a buyer will ask how the formula is protected and will not be reassured by a folder of signed NDAs and a shared drive open to everyone.",
    majorTerms: [
      "Designation process for information treated as secret",
      "Access tiers and a named-holder register",
      "Marking, storage and transmission rules",
      "Physical controls at facilities and for samples",
      "Onboarding and exit procedures",
      "Contractor and supply-chain flow-down requirements",
      "Incident reporting and response",
      "Periodic review and re-designation",
      "Record of who holds what, maintained over time",
    ],
    clausesToWatch: [
      {
        clause: "Designation criteria",
        why: "Designating everything as secret is functionally the same as designating nothing, and it undermines the case that any particular item was protected.",
      },
      {
        clause: "Access register",
        why: "Without a maintained list of who holds the formula, you cannot answer a buyer's diligence question or run a credible exit process.",
      },
      {
        clause: "Exit procedure",
        why: "Departures are when information leaves, and an undocumented exit means no record of what was returned or deleted.",
      },
      {
        clause: "Supply-chain flow-down",
        why: "Your programme stops at your own door unless co-manufacturers, labs and 3PLs are required to apply comparable controls.",
      },
      {
        clause: "Incident response",
        why: "A slow, undocumented response to a suspected leak weakens both your commercial position and any later claim.",
      },
    ],
    cpgIssues: [
      "The genuinely secret items in a CPG business are usually a short list: the full formula with usage rates, supplier identities and pricing, tolling rates, and account-level margin — designate those rather than the whole drive.",
      "Split-knowledge practices are common: no single co-manufacturer employee sees both the flavor system source and the full base formula.",
      "Pre-blends and coded compounds are a practical control, since the plant runs a numbered ingredient rather than the underlying build.",
      "Plant visitors, auditors and photographers all pass through areas where formulation and process are visible, so visitor controls belong in the programme.",
      "Sample retains held at labs and plants are physical copies of the product and need a retention and destruction schedule.",
      "Retailers and distributors receive spec sheets routinely; maintain a designated external version so nobody improvises one under deadline.",
    ],
    founderMistakes: [
      "Keeping the master formula in a cloud folder shared by open link, which meant anyone who had ever received it still had access.",
      "Marking every document confidential, which made the designation meaningless when it mattered.",
      "Never running an exit process, so a departing operations lead kept the supplier list and the tolling rates with no record of what left.",
      "Answering a buyer's diligence question about trade secret protection with signed NDAs and nothing else, which drove a purchase-price holdback.",
    ],
    negotiationPoints: [
      "Reduce the designated list to what genuinely needs protecting, and enforce it consistently.",
      "Ask co-manufacturers and labs to confirm in writing which of their personnel can access your formula.",
      "Require comparable controls from contractors as a condition of engagement rather than an afterthought.",
      "Build an exit checklist covering devices, cloud accounts, printed material and physical samples.",
    ],
    redFlags: [
      "The complete formula accessible to everyone in the company.",
      "No record of which external parties hold the formula.",
      "Shared logins to systems containing specifications or cost models.",
      "No exit process for employees or contractors who held secret material.",
      "Suppliers and co-manufacturers under no obligation to control internal access.",
    ],
    beforeYouSign: [
      "A current inventory of what the company treats as secret and where each item lives.",
      "A list of every external party holding a full formula copy.",
    ],
    related: [
      "recipe-formula-confidentiality-agreement",
      "formula-ownership-agreement",
      "employee-nda",
      "piia",
      "manufacturer-nda",
      "confidentiality-breach-notice",
    ],
    attorneyReview: "yes",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "product-development-nda",
    name: "Product Development & Confidentiality Agreement",
    category: "confidentiality",
    subcategory: "Formula and product IP",
    aliases: ["Development NDA", "Formulation NDA", "R&D Confidentiality Agreement"],
    stages: ["idea", "development", "pre-launch"],
    importance: "essential",
    entityTypes: ["any"],
    parties: "Brand + Developer, Formulator or Lab",
    whenYouNeedIt:
      "At the first substantive conversation with anyone who might help build the product, before a brief, a benchmark sample or a target cost goes across.",
    whatItDoes:
      "Combines confidentiality and non-use for the development conversation with a short, clear statement of who will own whatever the work produces — signed before the work starts rather than after it succeeds.",
    whyItMatters:
      "Ownership of what gets invented is decided before the work begins, not after it works. This document exists because the development conversation happens weeks before anyone drafts a services agreement, and by then a brief, a benchmark, a target price and two rounds of bench samples have already changed hands. A short combined document at the front of the relationship prevents the most common and most expensive outcome in CPG development: a successful formula with a genuinely contested owner.",
    majorTerms: [
      "Mutual or one-way confidentiality, matched to the actual disclosure",
      "Non-use covering the developer's other clients",
      "Statement that foreground work product will be assigned to the brand",
      "Identification of the developer's pre-existing background material",
      "Ownership of bench samples, notes and trial records",
      "Restriction on developing a similar product for others during the project",
      "Term of the exploratory phase and what happens if it does not proceed",
      "Survival of confidentiality regardless of whether a project follows",
      "Commitment to sign a full development agreement before formal work begins",
    ],
    clausesToWatch: [
      {
        clause: "Ownership if the project stops early",
        why: "Abandoned projects still generate usable formulations, and without a clause the half-finished work sits with the developer.",
      },
      {
        clause: "Bench notes and trial records",
        why: "The notes are where the useful knowledge is; a document that transfers only the final formula leaves the reasoning behind.",
      },
      {
        clause: "Background material identification",
        why: "Left undefined at this stage, the developer can later characterise the core of the solution as pre-existing.",
      },
      {
        clause: "Restriction on parallel competing projects",
        why: "A developer running your project and a competitor's brief in the same month is the ordinary situation, not the exception.",
      },
      {
        clause: "Bridge to the full agreement",
        why: "Without a commitment to paper the real agreement, this document quietly becomes the whole relationship.",
      },
    ],
    cpgIssues: [
      "Benchmarking is standard practice: you send a competitor's product and yours, and the developer analyses both — say who owns the analytical results.",
      "Target cost per unit and target retail price disclosed in a brief tell a developer more about your business than the formula does.",
      "Bench samples are physical prototypes that can be retained, tasted by other clients or photographed; cover their handling explicitly.",
      "Ingredient supplier introductions made during development are commercially valuable and often the developer's own relationships.",
      "Claims ambitions disclosed in a brief — protein content, sugar level, clean label targets — reveal the positioning before you have launched.",
    ],
    founderMistakes: [
      "Running four months of development on emails and a verbal understanding, then negotiating ownership only once the formula worked and leverage had shifted.",
      "Sending a full brief with target cost and target claims to three formulators to compare approaches, with nothing signed.",
      "Letting the developer keep all bench samples and notes when the project paused, then restarting with a different lab from zero.",
      "Assuming the development NDA covered ownership when it only covered disclosure.",
    ],
    negotiationPoints: [
      "Put a one-line ownership statement in the NDA itself rather than deferring it to a later agreement.",
      "Ask for bench notes, trial records and retained samples to be delivered or destroyed at your election.",
      "Agree what happens to work product if the project ends before a full agreement is signed.",
      "Ask the developer to identify background material in writing before the first bench trial.",
      "Set a short exclusivity window on the specific brief rather than a general non-compete.",
    ],
    redFlags: [
      "A developer who will not address ownership until after the work is done.",
      "Silence on what happens if the project is abandoned.",
      "Retention of all samples and notes by the developer as a matter of policy.",
      "A confidentiality obligation that lapses if no project proceeds.",
    ],
    beforeYouSign: [
      "The brief you intend to send, reviewed for anything that does not need to go out yet.",
      "A view on whether you want ownership of the formula or only the right to use it, and a willingness to pay accordingly.",
    ],
    related: [
      "product-development-agreement",
      "formula-development-agreement",
      "product-development-ip-agreement",
      "food-scientist-agreement",
      "rd-agreement",
      "mutual-nda",
    ],
    attorneyReview: "yes",
    templateId: "product-development-nda",
    templateVersion: "1.0",
    lastReviewed: "2026-08-25",
  },

  {
    id: "design-ownership-agreement",
    name: "Design Ownership Agreement",
    category: "confidentiality",
    subcategory: "Creative and brand assets",
    aliases: ["Brand Design Ownership Agreement", "Creative Assets Ownership Agreement", "Logo Ownership Agreement"],
    stages: ["idea", "pre-launch", "launch", "growth"],
    importance: "essential",
    entityTypes: ["any"],
    parties: "Brand + Designer or Design Studio",
    whenYouNeedIt:
      "Before a designer starts on the identity, the logo, the packaging system or the brand book — and as a cleanup document when a brand already launched on artwork that was never assigned.",
    whatItDoes:
      "Transfers ownership of the design work to the brand, defines the deliverables to include editable source files, and sorts out the third-party fonts, stock and illustration embedded in the work.",
    whyItMatters:
      "The most common version of this failure is quiet and expensive: the founder paid the designer, launched the brand, and cannot get the layered files back three years later when the label needs a nutrition panel change. The designer is not being difficult — nothing obliged them to hand over the working files or to keep them at all. Meanwhile the brand cannot reprint without paying a second studio to rebuild the artwork from a flattened PDF, on a printer's deadline.",
    majorTerms: [
      "Assignment of all design deliverables to the brand",
      "Definition of deliverables including layered and source files",
      "Font, stock and illustration licences and who holds them",
      "Right to modify, adapt and extend the design system",
      "Brand book, colour specifications and print standards",
      "File handover format and timing",
      "Portfolio and pre-launch embargo terms",
      "Warranty of originality and non-infringement",
      "Subcontractor rights secured by the studio",
      "Ongoing support or handover to an internal team",
    ],
    clausesToWatch: [
      {
        clause: "Definition of final deliverables",
        why: "Flattened exports look like a complete handover and leave you unable to change a single line of type later.",
      },
      {
        clause: "Font licensing",
        why: "Fonts are licensed, not owned, and a licence held by the studio does not transfer with the artwork unless it is dealt with.",
      },
      {
        clause: "Right to modify",
        why: "Without an express right to adapt the work, extending the design system to a new format can require going back to the original studio.",
      },
      {
        clause: "Retained rights in 'design elements'",
        why: "A studio reserving rights in underlying elements can leave you owning a lockup built on components you do not control.",
      },
      {
        clause: "Portfolio publication timing",
        why: "Unreleased packaging on a public portfolio reaches competitors and retailers before your launch does.",
      },
    ],
    cpgIssues: [
      "Packaging artwork is built against a converter's dieline, so the file only has value alongside the correct dieline and print specification.",
      "Colour must be specified in a form a printer can hold across substrates and runs, which means spot colour and print standard documentation, not a screen value.",
      "Label revisions are routine in CPG — ingredient changes, net content, claims updates — and each one needs the editable file.",
      "Retailer and marketplace portals demand specific artwork formats and image specifications that require source access to produce.",
      "Design systems in CPG have to stretch across can, bottle, carton, shipper and shelf-ready packaging, so the licence or ownership must cover formats not yet launched.",
      "Illustration commissioned for one flavour tends to become a family across the line, which needs rights broad enough to extend.",
    ],
    founderMistakes: [
      "Paying for a full brand identity and receiving only PDFs and PNGs, then paying again to have the logo redrawn as a vector for embroidery and packaging.",
      "Launching on a font licensed only for desktop use, and having to change the wordmark after the licensor raised it.",
      "Losing contact with the original designer, with the only editable artwork on their machine.",
      "Accepting a studio's retained rights in the illustration, then being unable to use it on a line extension without a further fee.",
    ],
    negotiationPoints: [
      "Require delivery of layered, editable source files and the dieline as a condition of final payment.",
      "Ask the studio to specify every font and stock asset used, with licence terms and who holds each licence.",
      "Secure an express, unrestricted right to modify and extend the design across all formats and future SKUs.",
      "Set the portfolio embargo to expire on public launch, and exclude anything showing costs or suppliers.",
      "Require the studio to secure and pass through the rights of any freelancer or illustrator they use.",
    ],
    redFlags: [
      "Deliverables described only as final files or approved artwork.",
      "Studio retention of rights in underlying design elements.",
      "No disclosure of fonts or stock assets.",
      "Ownership contingent on payment of future retainer fees.",
      "A design system delivered with no print specification or colour standard.",
    ],
    beforeYouSign: [
      "A deliverables schedule naming file formats, source files and the dieline.",
      "The font and stock asset list with licence terms attached.",
      "Confirmation that the printer can work from what you are being given.",
    ],
    related: [
      "packaging-artwork-ownership",
      "work-for-hire-agreement",
      "packaging-design-agreement",
      "graphic-design-agreement",
      "copyright-assignment",
      "branding-agency-agreement",
    ],
    attorneyReview: "depends",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "packaging-artwork-ownership",
    name: "Packaging Artwork Ownership Agreement",
    category: "confidentiality",
    subcategory: "Creative and brand assets",
    aliases: ["Artwork Ownership Agreement", "Dieline Ownership Agreement", "Label Artwork Rights Agreement"],
    stages: ["pre-launch", "manufacturing", "launch", "growth"],
    importance: "essential",
    entityTypes: ["any"],
    parties: "Brand + Designer, Printer or Converter",
    whenYouNeedIt:
      "Before the first print run, and again whenever a new converter, printer or co-manufacturer becomes involved in producing your packaging.",
    whatItDoes:
      "Settles ownership of the production artwork package — the print-ready files, the dieline, the colour separations and the prepress work — and secures your right to take that package to a different printer.",
    whyItMatters:
      "The artwork you can actually print with is not the same file the designer showed you. Between design and press sits a prepress step — trapping, separations, plate files, a converter-specific dieline — and that work is often done by the printer and treated as theirs. The result is a brand that owns its design and still cannot move printers without redoing prepress, at cost and on a lead time that does not fit between a co-packer's production window and a retailer's on-shelf date.",
    majorTerms: [
      "Ownership of the design artwork and of the production files separately",
      "Ownership or licence of the dieline and structural drawing",
      "Colour separations, trapping and plate files",
      "Right to transfer artwork to an alternative printer",
      "Plate and tooling ownership and storage",
      "Version control and approval records",
      "Retention period for artwork and plates",
      "Handling of label revisions and reprints",
      "Confidentiality of unreleased artwork",
    ],
    clausesToWatch: [
      {
        clause: "Prepress and separation ownership",
        why: "The printer may treat this as their work product, which is what actually stops you moving the job.",
      },
      {
        clause: "Dieline rights",
        why: "Dielines are often specific to a converter's tooling, so ownership and a right to adapt matter more than the file itself.",
      },
      {
        clause: "Plate ownership and storage",
        why: "If plates you paid for are treated as the printer's property, a reprint elsewhere means paying for plates twice.",
      },
      {
        clause: "Artwork retention on termination",
        why: "A printer with no obligation to release files on exit holds your ability to reprint hostage at the worst moment.",
      },
      {
        clause: "Version control",
        why: "Without a controlled current version, a reprint can pull an outdated ingredient statement or an expired claim onto shelf.",
      },
    ],
    cpgIssues: [
      "Label and carton minimums are large and lead times are long, so being unable to move printers quickly turns into stranded inventory or missed on-shelf dates.",
      "A dieline is tied to specific converting equipment, so moving printers usually means adapting the artwork even when you own it.",
      "Colour matching across substrates — film, paper, direct print on can — requires the print standard and approved proofs, not just the design file.",
      "Regulatory-driven label revisions arrive with deadlines, and each one requires the editable production file and a controlled approval trail.",
      "Co-manufacturers often manage label purchasing on your behalf, which can leave the artwork relationship sitting between the plant and the printer with you outside it.",
      "Shipper cases, shelf-ready packaging and multipack sleeves each carry their own artwork and are routinely forgotten in an ownership discussion focused on the primary label.",
    ],
    founderMistakes: [
      "Discovering at the point of a printer change that the separations and plate files belonged to the incumbent printer, delaying a reprint past the retailer's date.",
      "Letting the co-packer own the printer relationship, so the artwork files sat with a vendor the brand had no contract with.",
      "Paying for plates and never asking who owned or stored them, then paying again at the new printer.",
      "Reprinting from an old file after a formulation change, and putting an incorrect ingredient statement on shelf.",
    ],
    negotiationPoints: [
      "Ask for ownership of production-ready artwork, separations and the dieline, or at minimum a transferable licence.",
      "Confirm in writing that plates and tooling you pay for are yours and will be released or destroyed on request.",
      "Require release of all current artwork files within a short, defined period after any termination.",
      "Establish a single controlled current version with a documented approval trail before the first run.",
      "Where the co-manufacturer manages printing, contract directly with the printer for artwork rights.",
    ],
    redFlags: [
      "Printer ownership of prepress work with no transfer right.",
      "Plates described as the printer's property despite being paid for by you.",
      "No obligation to release artwork on termination.",
      "Artwork held only by the co-manufacturer with no direct relationship between you and the printer.",
      "No version control process before a reprint.",
    ],
    beforeYouSign: [
      "A written list of every artwork asset by SKU and pack component, and who holds each one.",
      "Confirmation from your printer of what a competing printer would need to run the job.",
      "The current approved artwork files in your own possession.",
    ],
    related: [
      "design-ownership-agreement",
      "packaging-specification-agreement",
      "label-supply-agreement",
      "packaging-design-agreement",
      "work-for-hire-agreement",
      "co-packer-agreement",
    ],
    attorneyReview: "yes",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "content-release",
    name: "Photographer / Video Content Release",
    category: "confidentiality",
    subcategory: "Content and likeness",
    aliases: ["Content Usage Release", "Photo Release", "Video Release", "Shoot Release"],
    stages: ["pre-launch", "launch", "growth"],
    importance: "essential",
    entityTypes: ["any"],
    parties: "Brand + Photographer or Videographer",
    whenYouNeedIt:
      "Before any paid shoot, and before using content a creator produced for you, including content shot on a phone at an event or in a kitchen.",
    whatItDoes:
      "Sets out what the brand may do with the resulting images and footage: which channels, for how long, in which territories, whether paid media is included, and whether the files may be edited.",
    whyItMatters:
      "This is the licensing side of the ownership question, and CPG founders get caught by it constantly. The standard photography deal is a licence, not a transfer, and licences are written by channel. Content licensed for organic social is frequently not licensed for paid ads, for Amazon and retailer listing images, for trade sell sheets, or for packaging. The brand runs the image as an ad because it is on their own drive, and the first they hear about the limit is an invoice or a takedown from the photographer's agent.",
    majorTerms: [
      "Whether the deal is a licence or an assignment",
      "Permitted channels: organic, paid, retailer listings, packaging, out-of-home",
      "Territory and duration of the grant",
      "Exclusivity — can the shooter licence the images elsewhere",
      "Right to edit, crop, recolour and create derivatives",
      "Delivery of raw files and outtakes",
      "Credit requirements",
      "Model, property and location releases secured by whom",
      "Renewal or extension terms and pricing",
      "Archive and re-licensing rights after the term",
    ],
    clausesToWatch: [
      {
        clause: "Paid media carve-out",
        why: "A licence covering 'social media' often excludes paid amplification, which is the use with actual budget attached.",
      },
      {
        clause: "Term of the licence",
        why: "A one-year term on hero product photography means your Amazon listing images expire while the SKU is still on shelf.",
      },
      {
        clause: "Packaging use",
        why: "Imagery used on-pack is almost always a separate, higher-priced grant, and an expired grant reaches shelf on printed inventory.",
      },
      {
        clause: "Right to edit",
        why: "Without it, cropping for a retailer template or recolouring for a new flavour can fall outside the licence.",
      },
      {
        clause: "Who secures releases",
        why: "If the photographer is not obliged to obtain model, property and location releases, the brand carries the risk of every face and location in frame.",
      },
      {
        clause: "Raw files",
        why: "A licence to delivered selects with no access to raw material means every future crop or re-master requires going back to the shooter.",
      },
    ],
    cpgIssues: [
      "Retailer and marketplace listing images have strict specifications and are commercially essential, so they need to be named as a permitted use rather than assumed.",
      "Product photography goes stale at every label revision, so tie the licence term to the pack generation rather than to a calendar year where you can.",
      "Shots showing food preparation or serving suggestions may imply claims about the product, and the marketing review of those images is separate from the licence question.",
      "Trade materials — sell sheets, distributor decks, shelf talkers, trade show booths — are a distinct use that consumer-focused licences frequently omit.",
      "Images containing other brands' products in frame, a recognisable retail environment or a private venue carry clearance issues the licence does not solve.",
      "Content produced at a co-manufacturer's facility may show their equipment and process, which is often subject to your confidentiality obligations to them.",
    ],
    founderMistakes: [
      "Running licensed lifestyle images as paid ads for a year before learning the licence covered organic use only.",
      "Building an Amazon listing on images with a fixed licence term, then having to reshoot to keep the listing live.",
      "Putting a shoot image on the pack under a licence that never mentioned packaging, discovered after a full label print run.",
      "Assuming the photographer had releases for the people in frame because the shoot looked professional.",
      "Losing the ability to re-crop for a new retailer template because only flattened selects were delivered.",
    ],
    negotiationPoints: [
      "Buy an outright assignment for hero product and packaging imagery; licence the lifestyle library if budget is tight.",
      "Name every channel explicitly, including paid social, marketplaces, retailer portals, trade materials and out-of-home.",
      "Ask for a perpetual term, or at minimum a term tied to the life of the pack.",
      "Require delivery of raw files and outtakes, and an express right to edit and create derivatives.",
      "Make the photographer responsible for obtaining and delivering all model, property and location releases.",
      "Agree extension pricing up front rather than at renewal, when your leverage is lower.",
    ],
    redFlags: [
      "A licence silent on paid media.",
      "A term shorter than the expected life of the SKU.",
      "No obligation on the shooter to obtain releases.",
      "Packaging use excluded or unaddressed.",
      "Mandatory credit on every use, including packaging and paid ads.",
      "No right to edit or crop the delivered files.",
    ],
    beforeYouSign: [
      "A written list of every place the content will be used, including channels you expect to add within two years.",
      "Confirmation of who is obtaining the model, property and location releases.",
      "The delivery specification: file types, raw material and turnaround.",
    ],
    related: [
      "model-release",
      "photography-agreement",
      "videography-agreement",
      "copyright-assignment",
      "ugc-creator-agreement",
      "nil-release",
    ],
    attorneyReview: "depends",
    templateId: "content-release",
    templateVersion: "1.0",
    lastReviewed: "2026-08-25",
  },

  {
    id: "model-release",
    name: "Model Release",
    category: "confidentiality",
    subcategory: "Content and likeness",
    aliases: ["Talent Release", "Appearance Release", "Likeness Release"],
    stages: ["pre-launch", "launch", "growth"],
    importance: "essential",
    entityTypes: ["any"],
    parties: "Brand + Individual appearing in the content",
    whenYouNeedIt:
      "Before anyone recognisable appears in content you will use commercially — a paid model, a friend on a shoot day, an employee, a customer at an event, a child in a family scene.",
    whatItDoes:
      "Records the individual's permission for the brand to use their image, voice and likeness in identified media, for a stated period and territory, in connection with the product.",
    whyItMatters:
      "The release that covers a shoot rarely covers a package. Using a person's face on primary packaging is a different and more permanent use than running them in a social post, and it is the use most releases silently omit. Once artwork prints, a limited release means either a costly relabel or product you cannot ship. The same gap appears with paid ads and with any implication that the person endorses the product rather than simply appearing in a photograph.",
    majorTerms: [
      "Scope of media: organic, paid, packaging, out-of-home, trade materials",
      "Territory and duration",
      "Whether the use may imply endorsement",
      "Right to edit, crop and composite the image",
      "Compensation and whether further payments are due for extended use",
      "Parent or guardian signature for minors",
      "Name and biographical use, if any",
      "Revocation terms, if any",
      "Confidentiality of unreleased product shown in the shoot",
    ],
    clausesToWatch: [
      {
        clause: "Packaging use",
        why: "A release covering advertising and social media commonly does not reach primary packaging, which is the longest-lived use of all.",
      },
      {
        clause: "Endorsement implication",
        why: "There is a real difference between appearing in a photograph and appearing to endorse a product, and the release should say which is permitted.",
      },
      {
        clause: "Duration and revocation",
        why: "A release the individual can withdraw creates a problem for printed inventory and for content already live in paid media.",
      },
      {
        clause: "Minors",
        why: "Content featuring children needs guardian consent and usually more careful scoping, particularly for anything consumed.",
      },
      {
        clause: "Right to composite and alter",
        why: "Retouching, compositing into a new background or placing the image alongside a claim can fall outside a narrowly drawn release.",
      },
    ],
    cpgIssues: [
      "A face on the pack is a long-term commitment tied to print runs and shelf life, so the release term needs to outlast the inventory, not the campaign.",
      "Consumption shots imply the person uses the product, which sits closer to endorsement than a lifestyle image does — particularly for supplements and functional products.",
      "Employees and founders appearing on pack need releases too, and those releases should survive their departure from the company.",
      "Event and sampling photography captures bystanders who never signed anything, which limits how that content can be used commercially.",
      "Athletes and anyone in a regulated or tested sport carry additional considerations for supplement and energy products that go well beyond a standard release.",
      "Content showing a person alongside a health or performance claim raises a marketing-review question that is separate from the likeness permission.",
    ],
    founderMistakes: [
      "Shooting a launch campaign with a friend on a handshake, then being unable to use the strongest images on pack.",
      "Using event photography containing identifiable bystanders in paid ads with no releases at all.",
      "Putting a founder's family member on the label under a release that expired before the next print run.",
      "Relying on a model agency's standard release without checking whether it covered packaging or only advertising.",
    ],
    negotiationPoints: [
      "Ask for a perpetual, worldwide release covering all media including packaging, or price a defined extension now.",
      "State explicitly whether endorsement is permitted, and match that to how the content will actually be used.",
      "Secure guardian signatures for anyone under age, with the scope written narrowly.",
      "Include a right to edit, retouch and composite.",
      "For anyone recognisable to your audience, use a fuller likeness agreement rather than a short release.",
    ],
    redFlags: [
      "A release limited to a single campaign when the image will appear on packaging.",
      "A revocation right with no notice period or wind-down for printed inventory.",
      "No guardian signature for a minor.",
      "Silence on paid media.",
      "An agency release that binds the agency but not the individual.",
    ],
    beforeYouSign: [
      "A clear decision on whether this person will appear on packaging.",
      "Signed releases collected on the shoot day rather than chased afterwards.",
    ],
    related: [
      "content-release",
      "nil-release",
      "photography-agreement",
      "ugc-creator-agreement",
      "influencer-agreement",
      "product-sampling-release",
    ],
    attorneyReview: "depends",
    templateId: "model-release",
    templateVersion: "1.0",
    lastReviewed: "2026-08-25",
  },

  {
    id: "nil-release",
    name: "Name / Image / Likeness Release",
    category: "confidentiality",
    subcategory: "Content and likeness",
    aliases: ["NIL Release", "Athlete Likeness Release", "Personality Rights Release"],
    stages: ["launch", "growth"],
    importance: "situational",
    entityTypes: ["any"],
    parties: "Brand + Athlete, Creator or Public Figure",
    whenYouNeedIt:
      "When someone with a following or a professional reputation will appear in your marketing or on your packaging — an athlete, a chef, a coach, a creator, a local celebrity.",
    whatItDoes:
      "Grants the brand permission to use a named individual's identity — name, image, likeness, voice, signature and sometimes nickname or jersey number — in defined media for a defined period.",
    whyItMatters:
      "A release covers permission; it does not cover the commercial deal, the compensation, the approvals or the morality terms that a real endorsement relationship needs. Founders use a short release for a partnership that is functionally an endorsement, and end up with no approval rights over how the athlete describes the product, no exclusivity in the category, and no ability to stop the images running after the relationship sours. For supplements and functional products the stakes are higher again, because an athlete's own governing body and existing sponsors may constrain what they can appear with.",
    majorTerms: [
      "Precise definition of the identity elements licensed",
      "Media, territory and term",
      "Whether packaging use is included",
      "Category exclusivity, if any",
      "Approval rights over creative and copy",
      "Compensation structure and any performance element",
      "Disclosure obligations for sponsored content",
      "Conduct and reputation provisions",
      "Post-term sell-off period for printed inventory",
      "Interaction with the individual's existing sponsorships and governing bodies",
    ],
    clausesToWatch: [
      {
        clause: "Post-term sell-off",
        why: "Without a wind-down window, printed packaging and live inventory become unsellable the day the term ends.",
      },
      {
        clause: "Approval rights",
        why: "Approval over every asset can stall a launch; no approval at all can put words in someone's mouth they will publicly disown.",
      },
      {
        clause: "Category exclusivity",
        why: "An athlete free to appear for a competing energy or supplement brand the following month undercuts the whole reason for the deal.",
      },
      {
        clause: "Conduct provisions",
        why: "This is the clause that decides whether you can stop using the imagery and stop paying if something happens off the field.",
      },
      {
        clause: "Existing obligations",
        why: "An individual bound by a team, league or prior sponsor may not be free to grant what they are granting.",
      },
      {
        clause: "Ingredient and testing considerations",
        why: "For supplements and energy products, an athlete's own testing obligations can make an appearance conditional on product certification.",
      },
    ],
    cpgIssues: [
      "Athletes subject to anti-doping testing frequently require third-party certification of a supplement or energy product before associating with it, and that testing has real cost and lead time.",
      "Packaging featuring an individual is printed months ahead and sits in inventory for months after, so the term and the sell-off window must cover the whole cycle.",
      "Performance and health claims made by the individual in their own words are still your marketing problem, so approvals and briefing matter.",
      "Category exclusivity in beverage is granular — energy, sports hydration, functional soda and protein can be treated as separate categories or one, and the definition drives the price.",
      "Retailer and marketplace listings featuring a person outlive campaigns; plan how listing images are refreshed at the end of a term.",
      "Sponsored-content disclosure obligations apply to the individual's own posts, and the brand usually carries the obligation to require them.",
    ],
    founderMistakes: [
      "Printing an athlete's image on packaging under a twelve-month release, then holding unsellable inventory when the term ended.",
      "Using a short release for what was really an endorsement, with no approval rights when the athlete described the product in terms the brand could not support.",
      "Signing with an athlete already contractually committed to a competing brand's category, which unwound the deal.",
      "Failing to require product certification for a tested athlete, which stopped the partnership before the first post.",
    ],
    negotiationPoints: [
      "Use a full likeness or endorsement agreement rather than a release wherever money and packaging are involved.",
      "Negotiate a sell-off period long enough to clear printed inventory, with a defined cut-off.",
      "Define category exclusivity precisely, listing included and excluded product types.",
      "Agree approval mechanics with a deemed-approval deadline so launches are not held up.",
      "Confirm in writing that the individual is free of conflicting commitments, and require notice of any new ones.",
    ],
    redFlags: [
      "Packaging use with no sell-off period.",
      "No representation that the individual is free of conflicting sponsorships.",
      "Unlimited approval rights with no response deadline.",
      "No conduct or reputation provision on a multi-year deal.",
      "A release signed by an agent with no evidence of authority.",
    ],
    beforeYouSign: [
      "Written confirmation of the individual's existing sponsorship and governing-body obligations.",
      "A decision on whether packaging use is in scope, before artwork is commissioned.",
      "The compensation and approval terms settled in the commercial agreement, not left to the release.",
    ],
    related: [
      "nil-agreement",
      "model-release",
      "celebrity-license-agreement",
      "ambassador-agreement",
      "influencer-agreement",
      "content-release",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "Personality rights, endorsement disclosure and athlete governing-body obligations vary by jurisdiction and by sport, and packaging use makes a mistake expensive to unwind.",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },
];

export default CONFIDENTIALITY_DOCS;
